import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ChatBubbleOutlineOutlined, VisibilityOffOutlined } from '@mui/icons-material';

import { api } from '../../../hooks/useApi';
import { LobbyHeaderInspector } from './panels/LobbyHeaderInspector';
import { PRIMARY, CANVAS_W, CANVAS_H, SHAPE_TYPES, uid, pct, slugify, CHAT_COLOR, LOBBY_BREAKPOINTS, resolveHotspotForBp, setHotspotBpOverride } from './constants';

import { DragGhost } from './canvas/DragGhost';
import { usePenTool, usePathEdit } from './canvas/PenTool';
import { applyResize, translateHotspot } from './canvas/SelectionGizmo';
import { LobbyCanvas, HotspotStripContents } from './canvas/LobbyCanvas';
import { CanvasToolbar } from './canvas/CanvasToolbar';
import { LayersPanel } from './panels/LayersPanel';
import { ElementPalette } from './panels/ElementPalette';
import { CanvasInspector } from './panels/CanvasInspector';
import { HotspotInspector } from './panels/HotspotInspector';
import { LobbyTabs } from './LobbyTabs';
import { PlenaryCanvas } from './canvas/PlenaryCanvas';
import { PlenaryInspector } from './panels/PlenaryInspector';
import { CreateLobbyOrPlenaryDialog } from './CreateLobbyOrPlenaryDialog';
import { ExhibitorCanvas } from './canvas/ExhibitorCanvas';
import { ExhibitorBoothEditor } from './ExhibitorBoothEditor';
import { ExhibitionInspector } from './panels/ExhibitionInspector';
import { CarouselSlidePanel } from './panels/CarouselSlidePanel';

import { ChatModal } from './ChatModal';
import { BuilderToolbar, PreviewCanvas } from './BuilderToolbar';
import { deserializeLobbyElement, serializeLobbyElement, deserializeLobbyHotspot, serializeLobbyHotspot } from './lobbySerializers';
import { useLobbyScreenshots } from './useLobbyScreenshots';

// ── Main export ───────────────────────────────────────────────────────────────
export default function LobbyBuilder({ event }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [lobbies, setLobbies] = useState([]);
  const [activeLobbyId, setActiveLobbyId] = useState(null);

  // ── Lobby header — default template is now 'pill' ─────────────────────────
  const DEFAULT_LOBBY_HEADER = {
    template: 'pill', sticky: false,
    logoSrc: event?.layout?.header?.logoSrc || event?.branding?.logoUrl || '', logoText: event?.name || 'Event', logoHeight: 36,
    links: [], accountLabel: '',
    style: { background: '#ffffff', linkColor: '#1a1a2e', accountBg: PRIMARY, accountColor: '#ffffff' },
  };
  const [lobbyHeaderConfig, setLobbyHeaderConfig] = useState(() => {
    const saved = event?.layout?.lobbyHeader || {};
    return {
      ...DEFAULT_LOBBY_HEADER,
      ...saved,
      // If lobbyHeader has no logoSrc saved, fall back to layout.header.logoSrc
      // then branding.logoUrl rather than showing a blank logo.
      logoSrc: saved.logoSrc || event?.layout?.header?.logoSrc || event?.branding?.logoUrl || '',
    };
  });
  // Always-current ref so closures (e.g. onChangeLinks) never capture stale config
  const lobbyHeaderConfigRef = useRef(lobbyHeaderConfig);
  useEffect(() => { lobbyHeaderConfigRef.current = lobbyHeaderConfig; }, [lobbyHeaderConfig]);
  const [headerSelected, setHeaderSelected] = useState(false);

  const selectId = useCallback((id) => {
    setSelectedId(id);
    setHeaderSelected(false);
  }, []);
  const [lobbiesLoading, setLobbiesLoading] = useState(true);

  const [plenaryPages, setPlenaryPages] = useState([]);
  const [activePlenaryId, setActivePlenaryId] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPlenaryComp, setSelectedPlenaryComp] = useState(null);

  const [exhibitionPages, setExhibitionPages] = useState([]);
  const [activeExhibitionId, setActiveExhibitionId] = useState(null);

  const [exhibitors, setExhibitors] = useState([]);
  const [exhibitorsLoading, setExhibitorsLoading] = useState(false);
  const [lobbyExhibitors, setLobbyExhibitors] = useState([]);
  const [selectedLobbyExhibitorId, setSelectedLobbyExhibitorId] = useState(null);
  const [deleteExhibitorConfirm, setDeleteExhibitorConfirm] = useState(null);
  const [canvasZoom, setCanvasZoom] = useState(.9);
  const [deleteLobbyConfirm, setDeleteLobbyConfirm] = useState(null);
  const [editingExhibitor, setEditingExhibitor] = useState(null);
  const [selectedExhibitorId, setSelectedExhibitorId] = useState(null);

  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [openChatHotspotId, setOpenChatHotspotId] = useState(null);

  // ── Responsive breakpoint ─────────────────────────────────────────────────
  // 'desktop' | 'tablet' | 'mobile'
  // Switching changes canvas preview size and which bg/positions are active.
  const [breakpoint, setBreakpoint] = useState('desktop');

  // Derived canvas dimensions for the active breakpoint
  const bpSpec    = LOBBY_BREAKPOINTS.find(b => b.key === breakpoint) || LOBBY_BREAKPOINTS[0];
  const bpCanvasW = bpSpec.canvasW;
  const bpCanvasH = bpSpec.canvasH;

  // { id, isActive: bool } — set when user tries to delete a stream element
  const [deleteStreamElementConfirm, setDeleteStreamElementConfirm] = useState(null);
  const [deletingStreamElement, setDeletingStreamElement] = useState(false);

  const [hotspotsByLobby, setHotspotsByLobby] = useState({});
  const [historyByLobby, setHistoryByLobby] = useState({});

  // ── Carousel slide state ──────────────────────────────────────────────────
  // For carousel lobbies, hotspots live inside lobby.slides[i].hotspots rather
  // than in the flat hotspotsByLobby map. We track which slide is being edited.
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Slide transition — tracks an in-progress crossfade between slides
  // { toIndex: number, direction: 'left'|'right', phase: 'enter'|'idle' }
  const [slideTransition, setSlideTransition] = useState(null);
  const slideTransitionTimer = useRef(null);

  const navigateToSlide = useCallback((toIndex, direction = 'right') => {
    if (slideTransitionTimer.current) clearTimeout(slideTransitionTimer.current);
    // Phase 1: mount the incoming slide layer (opacity 0, translated)
    setSlideTransition({ toIndex, direction, phase: 'enter' });
    // Phase 2: one frame later, trigger the CSS transition to animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSlideTransition(prev => prev ? { ...prev, phase: 'animate' } : null);
      });
    });
    // Phase 3: after animation completes, commit the index and remove overlay
    slideTransitionTimer.current = setTimeout(() => {
      setActiveSlideIndex(toIndex);
      setSlideTransition(null);
    }, 380);
  }, []);

  const [previewMode, setPreviewMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [selectedId, setSelectedId] = useState(null);

  const dragTypeRef = useRef(null);
  const [ghost, setGhost] = useState(null);
  const [activeTool, setActiveTool] = useState('pointer');
  const canvasRef = useRef(null);
  const [drawState, setDrawState] = useState(null);
  const draggingHotspot = useRef(null);

  // ── Screenshots — capture canvas thumbnails, cache in localStorage ────────
  const lobbyIds = useMemo(() => lobbies.map(l => l._id), [lobbies]);
  // Always-current ref so capture timers never read a stale activeLobbyId
  const activeLobbyIdRef = useRef(activeLobbyId);
  useEffect(() => { activeLobbyIdRef.current = activeLobbyId; }, [activeLobbyId]);

  // Ref-based flags so callbacks closed over them never hit TDZ
  const isCarouselRef      = useRef(false);
  const activeSlideIdxRef  = useRef(0);

  // Ensure slide 0 is visible before a carousel screenshot is taken.
  // Returns a promise that resolves once the slide is fully committed in the DOM.
  const ensureSlide0 = useCallback(() => {
    if (activeSlideIdxRef.current === 0) {
      // Already on slide 0 — wait 2s for card entry animations to finish
      return new Promise(r => setTimeout(r, 2000));
    }
    navigateToSlide(0, 'left');
    // 380ms (slide transition) + 2000ms (card entry animations) = 2380ms
    return new Promise(r => setTimeout(r, 2380));
  }, [navigateToSlide]);

  const getIsCarousel = useCallback(() => isCarouselRef.current, []);

  const { screenshots, scheduleCapture, captureNow, deleteScreenshot } = useLobbyScreenshots(
    canvasRef, activeLobbyIdRef, lobbyIds, ensureSlide0, getIsCarousel
  );

  // ── Load lobbies + plenaries + exhibitions ────────────────────────────────
  useEffect(() => {
    if (!event?._id) { setLobbiesLoading(false); return; }

    Promise.all([
      api.get(`/lobbies?eventId=${event._id}`),
      api.get(`/plenary?eventId=${event._id}`),
      api.get(`/exhibition?eventId=${event._id}`),
    ]).then(([lobbyRes, plenaryRes, exhibitionRes]) => {
      const sorted = lobbyRes.data?.success && lobbyRes.data.data?.length
        ? [...lobbyRes.data.data].sort((a, b) => (a.isDefault ? -1 : 1))
        : [];
      setLobbies(sorted);
      const byLobby = {};
      const histMap = {};
      sorted.forEach(l => {
        const hs = (l.hotspots || []).map(deserializeLobbyHotspot);
        const els = (l.elements || []).map(deserializeLobbyElement);
        const elIds = new Set(els.map(e => e.id));
        const all = [...hs.filter(h => !elIds.has(h.id)), ...els];
        byLobby[l._id] = all;
        histMap[l._id] = { stack: [all], idx: 0 };

        // Deserialize hotspots/elements inside each carousel slide
        if (l.lobbyType === 'carousel' && l.slides?.length) {
          l.slides = l.slides.map(s => {
            const sHs  = (s.hotspots || []).map(deserializeLobbyHotspot);
            const sEls = (s.elements || []).map(deserializeLobbyElement);
            return { ...s, hotspots: sHs, elements: sEls };
          });
        }
      });
      setHotspotsByLobby(byLobby);
      setHistoryByLobby(histMap);

      const plenaries = plenaryRes.data?.success ? (plenaryRes.data.data || []) : [];
      const exhibitions = exhibitionRes.data?.success ? (exhibitionRes.data.data || []) : [];
      setPlenaryPages(plenaries);
      setExhibitionPages(exhibitions);

      const urlId    = searchParams.get('id');
      const urlBooth = searchParams.get('booth');
      const urlSlide = searchParams.get('slide');
      if (urlId) {
        const matchedLobby = sorted.find(l => l._id === urlId);
        if (matchedLobby) {
          setActiveLobbyId(urlId);
          // Restore slide position for carousel lobbies
          if (matchedLobby.lobbyType === 'carousel' && urlSlide && matchedLobby.slides?.length) {
            const slideIdx = matchedLobby.slides.findIndex(s => s.id === urlSlide);
            if (slideIdx >= 0) setActiveSlideIndex(slideIdx);
          }
        } else if (plenaries.find(p => p._id === urlId)) {
          setActivePlenaryId(urlId);
          setActiveLobbyId(null);
        } else if (exhibitions.find(e => e._id === urlId)) {
          setActiveExhibitionId(urlId);
          setActiveLobbyId(null);
          if (urlBooth) {
            api.get(`/exhibitors/${urlBooth}`)
              .then(({ data: res }) => { if (res.success && res.data) setEditingExhibitor(res.data); })
              .catch(() => { });
          }
        } else {
          if (sorted.length) setActiveLobbyId(sorted[0]._id);
        }
      } else {
        if (sorted.length) setActiveLobbyId(sorted[0]._id);
        // If no lobbies at all, activeLobbyId stays null → wizard shows
      }

      setLobbiesLoading(false);

      // After load: queue a capture for any lobby that has no cached screenshot.
      // Use a short stagger so we don't fire them all simultaneously.
      setTimeout(() => {
        sorted.forEach((l, i) => {
          const cached = localStorage.getItem('gv_lobby_screenshot_' + l._id);
          if (!cached) {
            // Only capture the active lobby immediately; others staggered but skipped
            // until user navigates to them (captureNow checks canvasRef which only
            // reflects the active lobby). Active lobby gets a 2s head-start.
            if (i === 0) setTimeout(() => captureNow(l._id), 2000);
          }
        });
      }, 0);
    }).catch(() => setLobbiesLoading(false));
  }, [event?._id]);

  // ── Capture on switch — fire immediately if the newly active lobby has no screenshot
  useEffect(() => {
    if (!activeLobbyId || lobbiesLoading) return;
    const cached = localStorage.getItem('gv_lobby_screenshot_' + activeLobbyId);
    if (!cached && !screenshots[activeLobbyId]) {
      captureNow(activeLobbyId);
    }
  }, [activeLobbyId]); // eslint-disable-line
  useEffect(() => {
    if (!event?._id || !activeExhibitionId) return;
    setExhibitorsLoading(true);
    api.get(`/exhibitors?eventId=${event._id}&exhibitionId=${activeExhibitionId}`)
      .then(({ data: res }) => { if (res.success) setExhibitors(res.data || []); })
      .catch(() => { })
      .finally(() => setExhibitorsLoading(false));
  }, [event?._id, activeExhibitionId]);

  const activeGridLobbyId = useMemo(() => {
    const hs = hotspotsByLobby[activeLobbyId] || [];
    return hs.some(h => h.hotspotType === 'exhibitor-grid') ? activeLobbyId : null;
  }, [hotspotsByLobby, activeLobbyId]);

  const selectedGridHotspot = useMemo(() =>
    (hotspotsByLobby[activeLobbyId] || []).find(h => h.id === selectedId && h.hotspotType === 'exhibitor-grid'),
    [hotspotsByLobby, activeLobbyId, selectedId]
  );

  useEffect(() => { if (!selectedGridHotspot) setSelectedLobbyExhibitorId(null); }, [selectedGridHotspot]);

  useEffect(() => {
    if (!event?._id || !activeGridLobbyId) { setLobbyExhibitors([]); return; }
    api.get(`/exhibitors?lobbyId=${activeGridLobbyId}`)
      .then(({ data: res }) => { if (res.success) setLobbyExhibitors(res.data || []); })
      .catch(() => { });
  }, [event?._id, activeGridLobbyId]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const activeLobby  = lobbies.find(l => l._id === activeLobbyId) || null;
  const isCarousel   = activeLobby?.lobbyType === 'carousel';
  // Keep screenshot refs current so callbacks don't capture stale values
  isCarouselRef.current     = isCarousel;
  activeSlideIdxRef.current = activeSlideIndex;

  // ── Sync active slide index to URL ──────────────────────────────────────────
  useEffect(() => {
    if (!activeLobbyId) return;
    if (isCarousel && activeLobby?.slides?.length) {
      const slideId = activeLobby.slides[activeSlideIndex]?.id;
      if (slideId) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('id', activeLobbyId);
          next.set('slide', slideId);
          return next;
        }, { replace: true });
      }
    } else {
      setSearchParams(prev => {
        if (!prev.get('slide')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('slide');
        return next;
      }, { replace: true });
    }
  }, [activeLobbyId, activeSlideIndex, isCarousel, activeLobby?.slides, setSearchParams]);

  // For carousel lobbies, hotspots/elements come from the active slide.
  // For all other lobby types, they come from the flat hotspotsByLobby map.
  const activeSlide  = isCarousel ? (activeLobby?.slides?.[activeSlideIndex] ?? null) : null;
  const hotspots     = isCarousel
    ? [...(activeSlide?.hotspots || []), ...(activeSlide?.elements || [])]
    : (hotspotsByLobby[activeLobbyId] || []);

  const hist = historyByLobby[activeLobbyId] || { stack: [[]], idx: 0 };
  const canvasSelected = selectedId === 'canvas';
  const selectedHotspot = (selectedId && selectedId !== 'canvas')
    ? hotspots.find(h => h.id === selectedId) || null
    : null;

  const activeChatHotspot = openChatHotspotId
    ? hotspots.find(h => h.id === openChatHotspotId && h.hotspotType === 'chat') || null
    : null;

  const lobbyChatHotspots = hotspots.filter(h => h.hotspotType === 'chat');

  const openChatModal = useCallback((hotspotId) => {
    setOpenChatHotspotId(hotspotId || null);
    setChatModalOpen(true);
  }, []);

  const closeChatModal = useCallback(() => { setChatModalOpen(false); }, []);

  // ── History helpers ───────────────────────────────────────────────────────
  const pushHistory = useCallback((lobbyId, newHotspots) => {
    setHotspotsByLobby(prev => ({ ...prev, [lobbyId]: newHotspots }));
    setHistoryByLobby(prev => {
      const h = prev[lobbyId] || { stack: [[]], idx: 0 };
      const next = h.stack.slice(0, h.idx + 1).concat([newHotspots]);
      return { ...prev, [lobbyId]: { stack: next, idx: next.length - 1 } };
    });
  }, []);

  const undo = useCallback(() => {
    if (!activeLobbyId || hist.idx === 0) return;
    const prev = hist.stack[hist.idx - 1];
    setHotspotsByLobby(b => ({ ...b, [activeLobbyId]: prev }));
    setHistoryByLobby(b => ({ ...b, [activeLobbyId]: { ...hist, idx: hist.idx - 1 } }));
  }, [activeLobbyId, hist]);

  const redo = useCallback(() => {
    if (!activeLobbyId || hist.idx >= hist.stack.length - 1) return;
    const next = hist.stack[hist.idx + 1];
    setHotspotsByLobby(b => ({ ...b, [activeLobbyId]: next }));
    setHistoryByLobby(b => ({ ...b, [activeLobbyId]: { ...hist, idx: hist.idx + 1 } }));
  }, [activeLobbyId, hist]);

  // ── Lobby settings ────────────────────────────────────────────────────────
  const handleUpdateLobby = useCallback((patch) => {
    if (!activeLobbyId) return;
    setLobbies(prev => prev.map(l => l._id === activeLobbyId ? { ...l, ...patch } : l));
    api.put(`/lobbies/${activeLobbyId}`, patch).catch(() => { });
    scheduleCapture(activeLobbyId);
  }, [activeLobbyId, scheduleCapture]);

  // ── Carousel slide handlers ───────────────────────────────────────────────

  /** Update fields on a single slide (e.g. label, bgUrl) */
  const handleUpdateSlide = useCallback((slideIdx, patch) => {
    if (!activeLobbyId || !isCarousel) return;
    setLobbies(prev => prev.map(l => {
      if (l._id !== activeLobbyId) return l;
      const slides = (l.slides || []).map((s, i) => i === slideIdx ? { ...s, ...patch } : s);
      return { ...l, slides };
    }));
  }, [activeLobbyId, isCarousel]);

  /** Update breakpoint bg override on a slide */
  const handleUpdateSlideResponsive = useCallback((slideIdx, bp, patch) => {
    if (!activeLobbyId || !isCarousel || !bp || bp === 'desktop') return;
    setLobbies(prev => prev.map(l => {
      if (l._id !== activeLobbyId) return l;
      const slides = (l.slides || []).map((s, i) => {
        if (i !== slideIdx) return s;
        return {
          ...s,
          responsive: { ...(s.responsive || {}), [bp]: { ...(s.responsive?.[bp] || {}), ...patch } },
        };
      });
      return { ...l, slides };
    }));
  }, [activeLobbyId, isCarousel]);

  /** Add a new blank slide */
  const handleAddSlide = useCallback(() => {
    if (!activeLobbyId || !isCarousel) return;
    const currentSlide = activeLobby?.slides?.[activeSlideIndex];
    const newSlide = {
      id: `slide-${Date.now().toString(36)}`,
      label: `Slide ${(activeLobby?.slides?.length || 0) + 1}`,
      order: activeLobby?.slides?.length || 0,
      isActive: true,
      bgType: currentSlide?.bgType || 'image',
      bgUrl: currentSlide?.bgUrl || null,
      hotspots: [], elements: [],
    };
    const updatedSlides = [...(activeLobby?.slides || []), newSlide];
    handleUpdateLobby({ slides: updatedSlides });
    // Animate into the new slide (always from the right)
    navigateToSlide(updatedSlides.length - 1, 'right');
    setSelectedId(null);
  }, [activeLobbyId, isCarousel, activeLobby, activeSlideIndex, handleUpdateLobby, navigateToSlide]);

  /** Add a slide and auto-link an exhibitor to it */
  /** Delete a slide (keeps at least 1) */
  const handleDeleteSlide = useCallback((slideIdx) => {
    if (!activeLobbyId || !isCarousel) return;
    const slides = activeLobby?.slides || [];
    const deletedSlide = slides[slideIdx];

    if (slides.length <= 1) {
      // Last slide — convert back to a normal lobby, inherit bg from the slide
      const inherited = {
        lobbyType: 'lobby',
        slides: [],
        bgType: deletedSlide?.bgType || 'image',
        bgUrl:  deletedSlide?.bgUrl  || null,
      };
      handleUpdateLobby(inherited);
      api.put(`/lobbies/${activeLobbyId}`, inherited).catch(() => {});
      setActiveSlideIndex(0);
      setSelectedId(null);
      return;
    }

    const updated = slides.filter((_, i) => i !== slideIdx).map((s, i) => ({ ...s, order: i }));
    handleUpdateLobby({ slides: updated });
    setActiveSlideIndex(prev => Math.min(prev, updated.length - 1));
    setSelectedId(null);
  }, [activeLobbyId, isCarousel, activeLobby, handleUpdateLobby]);

  /** Reorder slides (drag-and-drop from CarouselSlidePanel) */
  const handleReorderSlides = useCallback((reordered) => {
    if (!activeLobbyId || !isCarousel) return;
    handleUpdateLobby({ slides: reordered });
  }, [activeLobbyId, isCarousel, handleUpdateLobby]);

  /** Remove exhibitor carousel — convert back to a regular lobby, clear slides */
  const handleRemoveCarousel = useCallback(() => {
    if (!activeLobbyId) return;
    // Take current slide's bg as the lobby bg when reverting
    const activeSlide = activeLobby?.slides?.[activeSlideIndex];
    const bgType = activeSlide?.bgType || activeLobby?.bgType || 'image';
    const bgUrl  = activeSlide?.bgUrl  || activeLobby?.bgUrl  || null;
    handleUpdateLobby({ lobbyType: 'lobby', slides: [], bgUrl, bgType });
    setActiveSlideIndex(0);
    selectId('canvas'); // keep the inspector open and canvas selected
    api.put(`/lobbies/${activeLobbyId}`, { lobbyType: 'lobby', slides: [], bgUrl, bgType }).catch(() => {});
  }, [activeLobbyId, activeLobby, activeSlideIndex, handleUpdateLobby, selectId]);

  /** Convert a regular lobby to a carousel, migrating its bg and hotspots into the first slide */
  const handleConvertToCarousel = useCallback(() => {
    if (!activeLobbyId || isCarousel) return;
    const existingHotspots = hotspots.filter(h => !['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
    const existingElements = hotspots.filter(h =>  ['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
    const firstSlide = {
      id: `slide-${Date.now().toString(36)}`,
      label: 'Slide 1', order: 0, isActive: true,
      bgType:     activeLobby?.bgType || 'image',
      bgUrl:      activeLobby?.bgUrl  || null,
      responsive: activeLobby?.responsive
        ? Object.fromEntries(Object.entries(activeLobby.responsive).map(([k, v]) => [k, { ...v }]))
        : undefined,
      hotspots: existingHotspots,
      elements: existingElements,
    };
    const firstSlideForServer = {
      ...firstSlide,
      hotspots: existingHotspots.map(serializeLobbyHotspot),
      elements: existingElements.map(serializeLobbyElement),
    };
    setLobbies(prev => prev.map(l => l._id === activeLobbyId
      ? { ...l, lobbyType: 'carousel', slides: [firstSlide], bgUrl: null, bgType: 'image', responsive: {}, hotspots: [], elements: [] }
      : l
    ));
    setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: [] }));
    setHistoryByLobby(prev => ({ ...prev, [activeLobbyId]: { stack: [[]], idx: 0 } }));
    api.put(`/lobbies/${activeLobbyId}`, {
      lobbyType: 'carousel', slides: [firstSlideForServer],
      bgUrl: null, bgType: 'image', responsive: {}, hotspots: [], elements: [],
    }).catch(() => {});
    setActiveSlideIndex(0);
    selectId('canvas');
  }, [activeLobbyId, isCarousel, activeLobby, hotspots, handleUpdateLobby, selectId]);

  /** Write hotspots back into the active slide */
  const pushCarouselHotspots = useCallback((slideIdx, allHotspots) => {
    if (!activeLobbyId || !isCarousel) return;
    const hotspotArr = allHotspots.filter(h => !['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
    const elementArr = allHotspots.filter(h =>  ['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
    setLobbies(prev => prev.map(l => {
      if (l._id !== activeLobbyId) return l;
      const slides = (l.slides || []).map((s, i) =>
        i === slideIdx ? { ...s, hotspots: hotspotArr, elements: elementArr } : s
      );
      return { ...l, slides };
    }));
  }, [activeLobbyId, isCarousel]);

  // ── pushHotspots: unified hotspot write for both carousel and regular lobbies
  // Defined after pushCarouselHotspots so both are in scope.
  const pushHotspots = useCallback((newHotspots) => {
    if (!activeLobbyId) return;
    if (isCarousel) {
      pushCarouselHotspots(activeSlideIndex, newHotspots);
    } else {
      pushHistory(activeLobbyId, newHotspots);
    }
    scheduleCapture(activeLobbyId);
  }, [activeLobbyId, isCarousel, activeSlideIndex, pushCarouselHotspots, pushHistory, scheduleCapture]);

  const handleUpdateEvent = useCallback((patch) => {
    if (!event?._id) return;
    api.put(`/events/${event._id}`, patch).catch(() => { });
  }, [event?._id]);

  const buildPayload = useCallback((lobby, hs) => ({
    name: lobby.name,
    slug: lobby.slug,
    isDefault: lobby.isDefault ?? false,
    isActive: lobby.isActive ?? true,
    lobbyType: lobby.lobbyType || 'lobby',
    bgType: lobby.bgType || 'image',
    bgUrl: lobby.bgUrl || null,
    showIntro: lobby.showIntro ?? true,
    introVideoUrl: lobby.introVideoUrl || null,
    transitionVideoUrl: lobby.transitionVideoUrl || null,
    returnVideoUrl: lobby.returnVideoUrl || null,
    responsive: lobby.responsive || {},
    event: event._id,
    hotspots: hs.filter(h => h.hotspotType !== 'stream' && h.hotspotType !== 'livestream-chat' && h.hotspotType !== 'exhibitor-grid').map(serializeLobbyHotspot),
    elements: hs.filter(h => h.hotspotType === 'stream' || h.hotspotType === 'livestream-chat' || h.hotspotType === 'exhibitor-grid').map(serializeLobbyElement),
    // Carousel: serialize hotspots/elements inside each slide before saving
    slides: lobby.lobbyType === 'carousel' ? (lobby.slides || []).map(s => ({
      ...s,
      hotspots: (s.hotspots || []).map(serializeLobbyHotspot),
      elements: (s.elements || []).map(serializeLobbyElement),
    })) : undefined,
  }), [event?._id]);

  // ── Save ──────────────────────────────────────────────────────────────────
  // ── Unified save ──────────────────────────────────────────────────────────
  // Detects the active context (lobby / plenary / exhibition) and runs the
  // correct write operations in a single call. lobbyHeaderConfig is always
  // persisted alongside a lobby save so inspector changes are never dropped.
  const handleSave = useCallback(async (hotspotOverride) => {
    if (!event?._id) return;
    setSaveStatus('saving');
    // Only use override when caller explicitly passes a valid array (not a DOM event)
    const hs = Array.isArray(hotspotOverride) ? hotspotOverride : hotspots;
    try {

      // ── Exhibition ────────────────────────────────────────────────────────
      if (activeExhibitionId && !editingExhibitor) {
        const page = exhibitionPages.find(p => p._id === activeExhibitionId);
        if (!page) throw new Error('Exhibition page not found');
        const { data: res } = await api.put(`/exhibition/${activeExhibitionId}`, page);
        if (!res?.success) throw new Error(res?.message || 'Update failed');

      // ── Plenary ───────────────────────────────────────────────────────────
      } else if (activePlenaryId) {
        const plenary = plenaryPages.find(p => p._id === activePlenaryId);
        if (!plenary) throw new Error('Plenary page not found');
        const { data: res } = await api.put(`/plenary/${activePlenaryId}`, plenary);
        if (!res?.success) throw new Error(res?.message || 'Update failed');

      // ── Lobby (default) ───────────────────────────────────────────────────
      } else if (activeLobbyId && activeLobby) {
        const payload = buildPayload(activeLobby, hs);
        const [{ data: res }] = await Promise.all([
          api.put(`/lobbies/${activeLobbyId}`, payload),
          // Always persist lobbyHeader alongside the lobby so inspector changes
          // made in the same session are never silently dropped.
          api.patch(`/events/${event._id}/layout`, { layout: { lobbyHeader: lobbyHeaderConfigRef.current } }),
        ]);
        if (!res?.success) throw new Error(res?.message || 'Update failed');

        // Ensure chat-channel IDs for any chat/livestream-chat hotspots
        const chatHotspots = hs.filter(h => h.hotspotType === 'chat' || h.hotspotType === 'livestream-chat');
        if (chatHotspots.length > 0) {
          const updates = await Promise.all(
            chatHotspots.map(async (h) => {
              try {
                const { data: chRes } = await api.post('/chat-channels/ensure', {
                  lobbyId: activeLobbyId,
                  hotspotId: h.id,
                  name: h.label || h.hotspotAction?.chatTitle || 'Live Chat',
                  eventId: event._id,
                  mode: h.hotspotAction?.mode || 'group',
                });
                if (chRes?.success && chRes.data?._id) {
                  const channelId = String(chRes.data._id);
                  if (!h.hotspotAction?.channelId) return { hotspotId: h.id, channelId };
                }
              } catch (err) {
                console.error('[LobbyBuilder] ensure channel error:', err);
              }
              return null;
            })
          );
          const patches = updates.filter(Boolean);
          if (patches.length > 0) {
            const patched = hs.map(h => {
              const match = patches.find(p => p.hotspotId === h.id);
              if (!match) return h;
              return { ...h, hotspotAction: { ...h.hotspotAction, channelId: match.channelId } };
            });
            setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: patched }));
            try { await api.put(`/lobbies/${activeLobbyId}`, buildPayload(activeLobby, patched)); }
            catch (err) { console.error('[LobbyBuilder] channel-id re-save error:', err); }
          }
        }
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
      // Schedule a screenshot 30s after the save — debounced so rapid saves only fire once
      if (activeLobbyId) scheduleCapture(activeLobbyId);
    } catch (err) {
      console.error('[LobbyBuilder] save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [
    event?._id,
    activeLobbyId, activeLobby, hotspots, buildPayload,
    activePlenaryId, plenaryPages,
    activeExhibitionId, exhibitionPages, editingExhibitor,
  ]);

  // ── Lobby CRUD ────────────────────────────────────────────────────────────
  const handleAddLobby = useCallback(async (dialogPayload) => {
    if (!event?._id) return;
    const typeLabels = { lobby: 'Lobby', plenary: 'Plenary', exhibition: 'Exhibition Hall', carousel: 'Carousel', booth: 'Booth' };
    const name = dialogPayload?.name || `${typeLabels[dialogPayload?._seedType] || 'Lobby'} ${lobbies.length + 1}`;
    const seedType = dialogPayload?._seedType || dialogPayload?.lobbyType || 'lobby';
    const { _seedType, ...cleanPayload } = dialogPayload || {};

    const PLENARY_BG = 'https://res.cloudinary.com/dasne3x79/image/upload/v1774418426/Screen_1.png';
    const EXHIBITION_BG = 'https://res.cloudinary.com/dasne3x79/image/upload/v1774481895/exhibitorbg.jpg';
    if (seedType === 'plenary' && !cleanPayload.bgUrl) cleanPayload.bgUrl = PLENARY_BG;

    // ── Exhibition: carousel with exhibitor-grid in slide 0, nav hidden ──────
    if (seedType === 'exhibition') {
      const exPayload = cleanPayload.name && cleanPayload.slug
        ? { ...cleanPayload, event: event._id, lobbyType: 'carousel' }
        : {
            name, slug: slugify(name), isDefault: !lobbies.length, isActive: true,
            bgType: 'image', bgUrl: null, showIntro: false,
            introVideoUrl: null, transitionVideoUrl: null, returnVideoUrl: null,
            lobbyType: 'carousel',
            event: event._id, hotspots: [], elements: [],
          };
      if (!exPayload.event) exPayload.event = event._id;
      if (!lobbies.length) exPayload.isDefault = true;

      const { data: exRes } = await api.post('/lobbies', exPayload);
      if (!exRes?.success) throw new Error(exRes?.message || 'Create failed');
      const newLobby = exRes.data;
      const newId = String(newLobby._id);

      const gridId = `exhibitor-grid-${Date.now().toString(36)}`;
      const gridElement = {
        id: gridId, label: 'Exhibitor Grid', shape: 'rect',
        points: [{ x: 10, y: 12.5 }],
        meta: {
          width: 80, height: 87.5, lobbyId: newId,
          gridTileSize: 200, gridGap: 8,
          gridCardTemplate: 'flat',
          gridDisplayMode: 'scroll',
        },
        responsive: {
          'tablet-landscape': { meta: { gridTileSize: 240, gridGap: 20, width: 80, height: 87.5 } },
          'tablet-portrait':  { meta: { gridTileSize: 500, gridGap: 40, width: 80, height: 87.5 } },
          'mobile':           { meta: { gridTileSize: 530, gridGap: 25, width: 80, height: 87.5 } },
        },
        hotspotType: 'exhibitor-grid',
        hotspotAction: { exhibitionId: null, lobbyId: newId },
        glowColor: '#0d7a5f', glowOpacity: 0, order: 0, isActive: true,
      };

      const firstSlide = {
        id: `slide-${Date.now().toString(36)}`,
        label: 'Exhibition Hall',
        order: 0, isActive: true,
        bgType: 'image', bgUrl: null,
        hideNav: true,
        hotspots: [],
        elements: [gridElement],
      };

      await api.put(`/lobbies/${newId}`, {
        lobbyType: 'carousel',
        slides: [{ ...firstSlide, elements: [serializeLobbyElement(gridElement)] }],
        bgUrl: null, bgType: 'image', hotspots: [], elements: [],
      });

      setLobbies(prev => [...prev, { ...newLobby, lobbyType: 'carousel', slides: [firstSlide] }]);
      setActiveLobbyId(newId);
      setActivePlenaryId(null);
      setActiveExhibitionId(null);
      setActiveSlideIndex(0);
      setHotspotsByLobby(prev => ({ ...prev, [newId]: [] }));
      setHistoryByLobby(prev => ({ ...prev, [newId]: { stack: [[]], idx: 0 } }));
      setSelectedId(null);
      setSearchParams({ id: newId }, { replace: true });
      captureNow(newId);
      return;
    }

    // Use cleanPayload only when it contains a full form submission (has name + slug)
    const payload = cleanPayload.name && cleanPayload.slug
      ? { ...cleanPayload, event: event._id }
      : {
        name, slug: slugify(name), isDefault: !lobbies.length, isActive: true,
        bgType: 'image', bgUrl: null, showIntro: false,
        introVideoUrl: null, transitionVideoUrl: null, returnVideoUrl: null,
        lobbyType: seedType,
        event: event._id, hotspots: [], elements: [],
      };

    // Ensure event is always set
    if (!payload.event) payload.event = event._id;
    // First lobby is always the default
    if (!lobbies.length) payload.isDefault = true;

    const { data: res } = await api.post('/lobbies', payload);
    if (!res?.success) throw new Error(res?.message || 'Create failed');
    const newLobby = res.data;
    const newId = String(newLobby._id);

    let seededHotspots = [];
    if (seedType === 'plenary') {
      const streamId = `stream-${Date.now().toString(36)}`;
      const chatId = `chat-${(Date.now() + 1).toString(36)}`;
      seededHotspots = [
        { id: streamId, label: 'Livestream Player', shape: 'rect', points: [{ x: 25, y: 25 }], meta: { width: 50.0, height: 50.8 }, hotspotType: 'stream', hotspotAction: { streamType: 'iframe', streamUrl: '', viewerCount: 0 }, glowColor: '#EF4444', glowOpacity: 0, order: 0, isActive: true },
        { id: chatId, label: 'Livestream Chat', shape: 'rect', points: [{ x: 80, y: 0 }], meta: { width: 20, height: 100, side: 'right', lobbyId: newId }, hotspotType: 'livestream-chat', hotspotAction: { channelId: null, chatTitle: 'Livestream Chat' }, glowColor: '#0a7aff', glowOpacity: 0, order: 1, isActive: true },
      ];
    }

    if (seededHotspots.length > 0) {
      const elements = seededHotspots.filter(h => ['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
      const hots = seededHotspots.filter(h => !['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
      await api.put(`/lobbies/${newId}`, { elements, hotspots: hots });
    }

    // Seed carousel with one blank slide
    if (seedType === 'carousel') {
      const firstSlide = { id: `slide-${Date.now().toString(36)}`, label: 'Slide 1', order: 0, isActive: true, bgType: 'image', bgUrl: null, hotspots: [], elements: [] };
      await api.put(`/lobbies/${newId}`, { slides: [firstSlide] });
      setLobbies(prev => prev.map(l => l._id === newId ? { ...l, slides: [firstSlide] } : l));
    }

    setLobbies(prev => [...prev, newLobby]);
    setActiveLobbyId(newId);
    setActivePlenaryId(null);
    setActiveExhibitionId(null);
    setHotspotsByLobby(prev => ({ ...prev, [newId]: seededHotspots.map(h => ({ ...h })) }));
    setHistoryByLobby(prev => ({ ...prev, [newId]: { stack: [seededHotspots], idx: 0 } }));
    setSelectedId(null);
    setSearchParams({ id: newId }, { replace: true });
    captureNow(newId);
  }, [lobbies.length, event?._id, setSearchParams]);

  const handleCreateExhibitorLobby = useCallback(async (exhibitor) => {
    if (!event?._id || !exhibitor) return;
    const name = `${exhibitor.name} Booth`;
    const slug = `exhibitor-booth-${exhibitor.slug || exhibitor._id}-${Date.now().toString(36)}`;
    const payload = {
      name, slug, isDefault: false, isActive: true,
      bgType: 'image', bgUrl: null, showIntro: false,
      introVideoUrl: null, transitionVideoUrl: null, returnVideoUrl: null,
      lobbyType: 'booth',
      event: event._id, hotspots: [], connectedExhibitor: exhibitor._id,
    };
    const { data: res } = await api.post('/lobbies', payload);
    if (!res?.success) throw new Error(res?.message || 'Create failed');
    const newLobby = res.data;
    const newId = String(newLobby._id);
    await api.put(`/exhibitors/${exhibitor._id}`, { boothLobbyId: newId });
    setLobbyExhibitors(prev => prev.map(ex => ex._id === exhibitor._id ? { ...ex, boothLobbyId: newId } : ex));
    setLobbies(prev => [...prev, newLobby]);
    setHotspotsByLobby(prev => ({ ...prev, [newId]: [] }));
    setHistoryByLobby(prev => ({ ...prev, [newId]: { stack: [[]], idx: 0 } }));
    setActiveLobbyId(newId);
    setActivePlenaryId(null);
    setSelectedId(null);
    setSearchParams({ id: newId }, { replace: true });
  }, [event?._id, lobbies.length, setSearchParams]);

  const handleGoToExhibitorLobby = useCallback((target) => {
    // target can be a plain lobby ID string (legacy) or { type:'slide', targetId } (new)
    if (target && typeof target === 'object' && target.type === 'slide') {
      // Navigate to the slide within the current carousel lobby with animation
      const slideIdx = (activeLobby?.slides || []).findIndex(s => s.id === target.targetId);
      if (slideIdx >= 0 && slideIdx !== activeSlideIndex) {
        navigateToSlide(slideIdx, slideIdx > activeSlideIndex ? 'right' : 'left');
        setSelectedId(null);
      }
      return;
    }
    const lobbyId = String(target);
    setActiveLobbyId(lobbyId);
    setActivePlenaryId(null);
    setActiveExhibitionId(null);
    setSelectedId(null);
    setSearchParams({ id: lobbyId }, { replace: true });
  }, [setSearchParams, activeLobby, activeSlideIndex, navigateToSlide]);

  const handleRenameLobby = useCallback((id, name) => {
    setLobbies(prev => prev.map(l => l._id === id ? { ...l, name, slug: slugify(name) } : l));
  }, []);

  const handleSetDefaultLobby = useCallback((id) => {
    setLobbies(prev => prev.map(l => ({ ...l, isDefault: l._id === id })));
    api.put(`/lobbies/${id}`, { isDefault: true }).catch(() => {});
  }, []);

  const handleReorderLobbies = useCallback((reordered) => {
    setLobbies(reordered);
    reordered.forEach((l, i) => { api.put(`/lobbies/${l._id}`, { order: i }).catch(() => { }); });
  }, []);

  const handleDuplicateLobby = useCallback(async (id) => {
    if (!event?._id) return;
    const src = lobbies.find(l => l._id === id);
    if (!src) return;
    const srcHotspots = hotspotsByLobby[id] || [];
    const cloneHotspots = srcHotspots.map(h => ({ ...h, id: uid() }));
    const payload = {
      name: `${src.name} (copy)`, slug: slugify(`${src.name}-copy`),
      isDefault: false, isActive: src.isActive ?? true,
      bgType: src.bgType || 'image', bgUrl: src.bgUrl || null,
      showIntro: src.showIntro ?? true,
      introVideoUrl: src.introVideoUrl || null,
      transitionVideoUrl: src.transitionVideoUrl || null,
      returnVideoUrl: src.returnVideoUrl || null,
      event: event._id,
      hotspots: cloneHotspots.map(serializeLobbyHotspot),
    };
    try {
      const { data: res } = await api.post('/lobbies', payload);
      if (!res?.success) throw new Error(res?.message || 'Duplicate failed');
      const clonedLobby = res.data;
      const cloneId = String(clonedLobby._id);
      const idx = lobbies.findIndex(l => l._id === id);
      setLobbies(prev => [...prev.slice(0, idx + 1), clonedLobby, ...prev.slice(idx + 1)]);
      setActiveLobbyId(cloneId);
      setHotspotsByLobby(prev => ({ ...prev, [cloneId]: cloneHotspots }));
      setHistoryByLobby(prev => ({ ...prev, [cloneId]: { stack: [cloneHotspots], idx: 0 } }));
      setSelectedId(null);
      setSearchParams({ id: cloneId }, { replace: true });
    } catch (err) {
      console.error('[LobbyBuilder] duplicate lobby error:', err);
    }
  }, [lobbies, hotspotsByLobby, event?._id, setSearchParams]);

  const commitDeleteLobby = useCallback(async (id) => {
    if (lobbies.length <= 1) return;
    const lobby = lobbies.find(l => String(l._id) === String(id));
    const idx = lobbies.findIndex(l => String(l._id) === String(id));
    const next = lobbies.filter(l => String(l._id) !== String(id));
    const nextActive = String(activeLobbyId) === String(id) ? next[Math.max(0, idx - 1)]._id : activeLobbyId;
    setLobbies(next);
    setActiveLobbyId(nextActive);
    setSelectedId(null);
    setDeleteLobbyConfirm(null);
    setSearchParams({ id: nextActive }, { replace: true });

    if (lobby?.lobbyType === 'booth' && lobby?.connectedExhibitor) {
      const exId = String(lobby.connectedExhibitor);
      setLobbyExhibitors(prev => prev.map(ex => String(ex._id) === exId ? { ...ex, boothLobbyId: null } : ex));
      setExhibitors(prev => prev.map(ex => String(ex._id) === exId ? { ...ex, boothLobbyId: null } : ex));
      api.put(`/exhibitors/${exId}`, { boothLobbyId: null }).catch(() => { });
    }

    api.delete(`/lobbies/${id}`).catch(() => { });
    deleteScreenshot(id);
  }, [lobbies, activeLobbyId, setSearchParams]);

  const handleDeleteLobby = useCallback(async (id) => {
    if (lobbies.length <= 1) return;
    const hs = hotspotsByLobby[id] || [];
    const hasGrid = hs.some(h => h.hotspotType === 'exhibitor-grid');
    if (hasGrid) {
      let count = 0;
      try {
        const { data: res } = await api.get(`/exhibitors?lobbyId=${id}`);
        count = res.success ? (res.data?.length ?? 0) : 0;
      } catch { }
      if (count > 0) {
        const lobby = lobbies.find(l => String(l._id) === String(id));
        setDeleteLobbyConfirm({ id, lobbyName: lobby?.name || 'this lobby', exhibitorCount: count });
        return;
      }
    }
    commitDeleteLobby(id);
  }, [lobbies, hotspotsByLobby, commitDeleteLobby]);

  const handleSwitchLobby = useCallback((id) => {
    setActiveLobbyId(id);
    setActivePlenaryId(null);
    setActiveExhibitionId(null);
    setEditingExhibitor(null);
    setSelectedExhibitorId(null);
    setSelectedId(null);
    setSelectedPlenaryComp(null);
    setActiveSlideIndex(0);
    setSearchParams({ id }, { replace: true });
  }, [setSearchParams]);

  // ── Lobby header ──────────────────────────────────────────────────────────
  const handleLobbyHeaderChange = useCallback(async (newConfig) => {
    setLobbyHeaderConfig(newConfig);
    try {
      // Use the dedicated PATCH /layout endpoint which does a dot-notation
      // $set so only layout.lobbyHeader is touched — other layout keys
      // (header, footer, etc.) are left intact.
      await api.patch(`/events/${event._id}/layout`, {
        layout: { lobbyHeader: newConfig },
      });
    } catch (err) {
      console.error('[LobbyHeader] save failed', err);
    }
  }, [event?._id]);

  const handleHotspotPreviewClick = useCallback((hotspot) => {
    const action = hotspot.hotspotAction;
    if (!action) return;
    if (hotspot.hotspotType === 'link') {
      if (action.linkTarget === 'external' && action.externalUrl) {
        const url = /^https?:\/\//i.test(action.externalUrl) ? action.externalUrl : `https://${action.externalUrl}`;
        window.open(url, action.openIn === 'blank' ? '_blank' : '_self');
        return;
      }
      if (action.targetId) handleSwitchLobby(action.targetId);
    }
    if (hotspot.hotspotType === 'chat') openChatModal(hotspot.id);
  }, [handleSwitchLobby, openChatModal]);

  // ── Plenary CRUD ──────────────────────────────────────────────────────────
  const handleAddPlenary = useCallback(async (payload) => {
    if (!event?._id) return;
    const { data: res } = await api.post('/plenary', { ...payload, event: event._id });
    if (!res?.success) throw new Error(res?.message || 'Create plenary failed');
    setPlenaryPages(prev => [...prev, res.data]);
    const newPlenaryId = String(res.data._id);
    setActivePlenaryId(newPlenaryId);
    setActiveLobbyId(null);
    setSelectedId(null);
    setSearchParams({ id: newPlenaryId }, { replace: true });
  }, [event?._id, setSearchParams]);

  const handleSwitchPlenary = useCallback((id) => {
    setActivePlenaryId(id);
    setActiveLobbyId(null);
    setActiveExhibitionId(null);
    setEditingExhibitor(null);
    setSelectedExhibitorId(null);
    setSelectedId(null);
    setSelectedPlenaryComp(null);
    setSearchParams({ id }, { replace: true });
  }, [setSearchParams]);

  const handleDeletePlenary = useCallback(async (id) => {
    try { await api.delete(`/plenary/${id}`); } catch (err) { console.error('[LobbyBuilder] delete plenary error:', err); }
    setPlenaryPages(prev => prev.filter(p => p._id !== id));
    if (activePlenaryId === id) {
      setActivePlenaryId(null);
      const fallbackId = lobbies[0]?._id || null;
      if (fallbackId) { setActiveLobbyId(fallbackId); setSearchParams({ id: fallbackId }, { replace: true }); }
      else setSearchParams({}, { replace: true });
    }
  }, [activePlenaryId, lobbies, setSearchParams]);

  const handleUpdatePlenary = useCallback((patch) => {
    if (!activePlenaryId) return;
    setPlenaryPages(prev => prev.map(p => p._id === activePlenaryId ? { ...p, ...patch } : p));
  }, [activePlenaryId]);


  // ── Exhibition CRUD ───────────────────────────────────────────────────────
  const handleAddExhibition = useCallback(async (payload) => {
    if (!event?._id) return;
    const { data: res } = await api.post('/exhibition', { ...payload, event: event._id });
    if (!res?.success) throw new Error(res?.message || 'Create exhibition failed');
    setExhibitionPages(prev => [...prev, res.data]);
    const newExhibitionId = String(res.data._id);
    setActiveExhibitionId(newExhibitionId);
    setActiveLobbyId(null);
    setActivePlenaryId(null);
    setEditingExhibitor(null);
    setSelectedId(null);
    setSearchParams({ id: newExhibitionId }, { replace: true });
  }, [event?._id, setSearchParams]);

  const handleSwitchExhibition = useCallback((id) => {
    setActiveExhibitionId(id);
    setActiveLobbyId(null);
    setActivePlenaryId(null);
    setEditingExhibitor(null);
    setSelectedId(null);
    setSelectedPlenaryComp(null);
    setSearchParams({ id }, { replace: true });
  }, [setSearchParams]);

  const handleOpenBoothEditor = useCallback((exhibitor) => {
    setEditingExhibitor(exhibitor);
    setSelectedExhibitorId(null);
    setSearchParams({ id: activeExhibitionId, booth: exhibitor._id }, { replace: true });
  }, [activeExhibitionId, setSearchParams]);

  const handleCloseBoothEditor = useCallback(() => {
    setEditingExhibitor(null);
    setSearchParams(activeExhibitionId ? { id: activeExhibitionId } : {}, { replace: true });
  }, [activeExhibitionId, setSearchParams]);

  const handleDeleteExhibition = useCallback(async (id) => {
    try { await api.delete(`/exhibition/${id}`); } catch (err) { console.error('[LobbyBuilder] delete exhibition error:', err); }
    setExhibitionPages(prev => prev.filter(p => p._id !== id));
    if (activeExhibitionId === id) {
      setActiveExhibitionId(null);
      setEditingExhibitor(null);
      const fallbackId = lobbies[0]?._id || null;
      if (fallbackId) { setActiveLobbyId(fallbackId); setSearchParams({ id: fallbackId }, { replace: true }); }
      else setSearchParams({}, { replace: true });
    }
  }, [activeExhibitionId, lobbies, setSearchParams]);

  const handleUpdateExhibition = useCallback((patch) => {
    if (!activeExhibitionId) return;
    setExhibitionPages(prev => prev.map(p => p._id === activeExhibitionId ? { ...p, ...patch } : p));
  }, [activeExhibitionId]);


  // ── Exhibitor CRUD ────────────────────────────────────────────────────────
  const handleUpdateExhibitor = useCallback(async (updated) => {
    setExhibitors(prev => prev.map(ex => ex._id === updated._id ? updated : ex));
    setLobbyExhibitors(prev => prev.map(ex => ex._id === updated._id ? updated : ex));
    try { await api.put(`/exhibitors/${updated._id}`, updated); } catch (err) { console.error('[LobbyBuilder] update exhibitor error:', err); }
  }, []);

  /** Add a slide and auto-link an exhibitor to it */
  const handleAddSlideForExhibitor = useCallback((ex) => {
    if (!activeLobbyId || !isCarousel || !ex) return;
    const currentSlide = activeLobby?.slides?.[activeSlideIndex];
    const newSlide = {
      id: `slide-${Date.now().toString(36)}`,
      label: ex.name || `Slide ${(activeLobby?.slides?.length || 0) + 1}`,
      order: activeLobby?.slides?.length || 0,
      isActive: true,
      bgType: currentSlide?.bgType || 'image',
      bgUrl: currentSlide?.bgUrl || null,
      hotspots: [], elements: [],
    };
    const updatedSlides = [...(activeLobby?.slides || []), newSlide];
    handleUpdateLobby({ slides: updatedSlides });
    navigateToSlide(updatedSlides.length - 1, 'right');
    setSelectedId(null);
    handleUpdateExhibitor({ ...ex, boothAction: { type: 'slide', targetId: newSlide.id } });
  }, [activeLobbyId, isCarousel, activeLobby, activeSlideIndex, handleUpdateLobby, handleUpdateExhibitor, navigateToSlide]);

  const handleDeleteExhibitorFromHall = useCallback(async (id, force = false) => {
    const ex = [...lobbyExhibitors, ...exhibitors].find(e => String(e._id) === String(id));
    if (!force && ex?.boothLobbyId) { setDeleteExhibitorConfirm(ex); return; }

    const boothLobbyId = ex?.boothLobbyId ? String(ex.boothLobbyId) : null;
    if (boothLobbyId) {
      setLobbies(prev => prev.filter(l => String(l._id) !== boothLobbyId));
      if (String(activeLobbyId) === boothLobbyId) {
        const remaining = lobbies.filter(l => String(l._id) !== boothLobbyId);
        const nextId = remaining[0]?._id;
        if (nextId) { setActiveLobbyId(String(nextId)); setSearchParams({ id: String(nextId) }, { replace: true }); }
      }
      try { await api.delete(`/lobbies/${boothLobbyId}`); } catch { }
    }

    setExhibitors(prev => prev.filter(e => String(e._id) !== String(id)));
    setLobbyExhibitors(prev => prev.filter(e => String(e._id) !== String(id)));
    setSelectedExhibitorId(null);
    setSelectedLobbyExhibitorId(null);
    setDeleteExhibitorConfirm(null);
    try { await api.delete(`/exhibitors/${id}`); } catch (err) { console.error('[LobbyBuilder] delete exhibitor error:', err); }
  }, [lobbyExhibitors, exhibitors, activeLobbyId, lobbies, setSearchParams]);

  const handleReorderExhibitors = useCallback(async (reordered) => {
    setExhibitors(reordered);
    reordered.forEach(ex => { api.put(`/exhibitors/${ex._id}`, { order: ex.order }).catch(() => { }); });
  }, []);

  const handleAddExhibitorToHall = useCallback(async () => {
    if (!event?._id) return;
    const flatHotspots = hotspotsByLobby[activeLobbyId] || [];
    // For carousel lobbies, check all slides for an exhibitor-grid element
    const carouselHasGrid = isCarousel && (activeLobby?.slides || []).some(s =>
      [...(s.hotspots || []), ...(s.elements || [])].some(h => h.elementType === 'exhibitor-grid' || h.hotspotType === 'exhibitor-grid')
    );
    const isGridContext = !activeExhibitionId && !!activeLobbyId && (flatHotspots.some(h => h.hotspotType === 'exhibitor-grid') || carouselHasGrid);
    if (!activeExhibitionId && !isGridContext) return;
    const n = (isGridContext ? lobbyExhibitors : exhibitors).length + 1;
    const name = `New Exhibitor ${n}`;
    const slug = `new-exhibitor-${n}-${Date.now().toString(36)}`;
    try {
      const { data: res } = await api.post('/exhibitors', {
        name, slug, event: event._id,
        ...(isGridContext ? { lobby: activeLobbyId } : { exhibition: activeExhibitionId }),
        order: n - 1, isActive: true, boothBgColor: '#1a1a2e',
      });
      if (!res?.success) throw new Error(res?.message);
      if (isGridContext) { setLobbyExhibitors(prev => [...prev, res.data]); }
      else { setExhibitors(prev => [...prev, res.data]); setSelectedExhibitorId(String(res.data._id)); }
    } catch (err) {
      console.error('[LobbyBuilder] add exhibitor error:', err);
    }
  }, [event?._id, activeExhibitionId, activeLobbyId, exhibitors.length, lobbyExhibitors.length, hotspotsByLobby]);

  const selectedExhibitor = selectedExhibitorId
    ? exhibitors.find(ex => ex._id === selectedExhibitorId) || null
    : null;

  // ── Hotspot CRUD ──────────────────────────────────────────────────────────
  // Removes a hotspot from history. For stream elements with a Mux stream,
  // shows a blocking modal and deletes the stream via API before removing the element.
  const commitDeleteHotspot = useCallback((id) => {
    if (!activeLobbyId) return;
    pushHotspots(hotspots.filter(h => h.id !== id));
    if (selectedId === id) setSelectedId(null);
    if (openChatHotspotId === id) { setChatModalOpen(false); setOpenChatHotspotId(null); }
  }, [activeLobbyId, hotspots, pushHotspots, selectedId, openChatHotspotId]);

  const handleDeleteHotspot = useCallback((id) => {
    const hotspot = hotspots.find(h => h.id === id);
    if (hotspot?.hotspotType === 'stream') {
      const muxStatus = hotspot.hotspotAction?.muxStatus;
      const isActive  = muxStatus === 'active' || muxStatus === 'idle';
      const hasMux    = !!hotspot.hotspotAction?.muxStreamId;
      setDeleteStreamElementConfirm({ id, isActive, hasMux, muxStreamId: hotspot.hotspotAction?.muxStreamId });
      return;
    }
    commitDeleteHotspot(id);
  }, [hotspots, commitDeleteHotspot]);

  const handleUpdateHotspot = useCallback((updated) => {
    if (!activeLobbyId) return;
    pushHotspots(hotspots.map(h => h.id === updated.id ? updated : h));
  }, [activeLobbyId, hotspots, pushHotspots]);

  // ── Canvas coordinate helpers ─────────────────────────────────────────────
  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = pct((e.clientX - rect.left) / rect.width * bpCanvasW, bpCanvasW);
    const y = pct((e.clientY - rect.top) / rect.height * bpCanvasH, bpCanvasH);
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  }, [bpCanvasW, bpCanvasH]);

  const getSvgCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * bpCanvasW, y: ((e.clientY - rect.top) / rect.height) * bpCanvasH };
  }, [bpCanvasW, bpCanvasH]);

  // ── Pen tool ──────────────────────────────────────────────────────────────
  const penTool = usePenTool({
    getCanvasCoords,
    onCommit: useCallback((hotspot) => {
      if (!activeLobbyId) return;
      const h = { ...hotspot, id: uid(), order: hotspots.length };
      pushHotspots([...hotspots, h]);
      selectId(h.id);
      setActiveTool('pointer');
    }, [activeLobbyId, hotspots, pushHotspots]),
  });

  const [editingHotspot, setEditingHotspot] = useState(null);

  const pathEdit = usePathEdit({
    getCanvasCoords,
    hotspot: editingHotspot,
    onCommit: useCallback((updated) => {
      if (!activeLobbyId) return;
      if (isCarousel) {
        pushCarouselHotspots(activeSlideIndex, hotspots.map(h => h.id === updated.id ? updated : h));
      } else {
        setHotspotsByLobby(prev => ({
          ...prev,
          [activeLobbyId]: (prev[activeLobbyId] || []).map(h => h.id === updated.id ? updated : h),
        }));
      }
    }, [activeLobbyId, isCarousel, activeSlideIndex, hotspots, pushCarouselHotspots]),
    onExit: useCallback(() => {
      if (!isCarousel) {
        setHotspotsByLobby(prev => {
          const list = prev[activeLobbyId] || [];
          setHistoryByLobby(hb => {
            const h = hb[activeLobbyId] || { stack: [[]], idx: 0 };
            return { ...hb, [activeLobbyId]: { stack: [...h.stack.slice(0, h.idx + 1), list], idx: h.idx + 1 } };
          });
          return prev;
        });
      }
      setEditingHotspot(null);
    }, [activeLobbyId, isCarousel]),
  });

  pathEdit.onEnter = useCallback((hotspot) => {
    setEditingHotspot(hotspot);
    setSelectedId(null);
  }, []);

  useEffect(() => { if (activeTool === 'pen' && editingHotspot) setEditingHotspot(null); }, [activeTool, editingHotspot]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'p' || e.key === 'P') setActiveTool(t => t === 'pen' ? 'pointer' : 'pen');
      if (e.key === 'Escape' && activeTool === 'pen') { penTool.reset(); setActiveTool('pointer'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTool, penTool]);

  const handleSelectTool = useCallback((key) => {
    if (key !== 'pen') penTool.reset();
    setActiveTool(key);
    setSelectedId(null);
    setDrawState(null);
  }, [penTool]);

  // ── Rect / Circle draw ────────────────────────────────────────────────────
  const handleDrawMouseDown = useCallback((e) => {
    if (activeTool !== 'rect' && activeTool !== 'circle') return;
    e.stopPropagation();
    const pt = getSvgCoords(e);
    if (!pt) return;
    setDrawState({ active: true, startX: pt.x, startY: pt.y, curX: pt.x, curY: pt.y });
  }, [activeTool, getSvgCoords]);

  const handleDrawMouseMove = useCallback((e) => {
    if (!drawState?.active) return;
    const pt = getSvgCoords(e);
    if (!pt) return;
    setDrawState(prev => ({ ...prev, curX: pt.x, curY: pt.y }));
  }, [drawState, getSvgCoords]);

  const handleDrawMouseUp = useCallback((e) => {
    if (!drawState?.active || !activeLobbyId) { setDrawState(null); return; }
    const { startX, startY, curX, curY } = drawState;
    setDrawState(null);
    const dx = Math.abs(curX - startX);
    const dy = Math.abs(curY - startY);
    if (dx < 2 && dy < 2) return;
    const id = uid();
    let h;
    if (activeTool === 'rect') {
      const x = pct(Math.min(startX, curX), bpCanvasW);
      const y = pct(Math.min(startY, curY), bpCanvasH);
      h = { id, label: 'Rectangle Hotspot', shape: 'rect', points: [{ x, y }], meta: { width: pct(dx, bpCanvasW), height: pct(dy, bpCanvasH) }, hotspotType: 'link', hotspotAction: {}, glowColor: SHAPE_TYPES.rect.color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    } else {
      const cx = pct(startX, bpCanvasW);
      const cy = pct(startY, bpCanvasH);
      const r = pct(Math.sqrt(dx * dx + dy * dy), bpCanvasW);
      h = { id, label: 'Circle Hotspot', shape: 'circle', points: [{ x: cx, y: cy }], meta: { radius: r }, hotspotType: 'link', hotspotAction: {}, glowColor: SHAPE_TYPES.circle.color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    }
    pushHotspots([...hotspots, h]);
    selectId(h.id);
    setActiveTool('pointer');
  }, [drawState, activeTool, activeLobbyId, hotspots, pushHotspots, bpCanvasW, bpCanvasH]);

  // ── Palette drag ──────────────────────────────────────────────────────────
  const handlePaletteDragStart = useCallback((e, type) => {
    dragTypeRef.current = type;
    const g = document.createElement('div');
    g.style.cssText = 'position:fixed;left:-9999px;top:0';
    document.body.appendChild(g);
    e.dataTransfer.setDragImage(g, 0, 0);
    setTimeout(() => document.body.removeChild(g), 0);
  }, []);

  useEffect(() => {
    const onMove = (e) => { if (dragTypeRef.current) setGhost({ type: dragTypeRef.current, x: e.clientX, y: e.clientY }); };
    const onUp = () => { if (dragTypeRef.current) { dragTypeRef.current = null; setGhost(null); } };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  // ── Canvas drop ───────────────────────────────────────────────────────────
  // ── Shared element-creation logic (used by both drag-drop and click-to-add) ─
  const createElementAtCoords = useCallback((type, coords) => {
    if (!type || !activeLobbyId) return;

    // ── Exhibitor Carousel — converts the lobby into a carousel ──────────────
    if (type === 'exhibitor-carousel') {
      if (isCarousel) return; // already a carousel
      // Migrate existing hotspots/elements into the first slide
      // Hotspots array is already deserialized (hotspotType/hotspotAction format)
      const existingHotspots = hotspots.filter(h => !['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));
      const existingElements = hotspots.filter(h =>  ['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType));

      // firstSlide stores deserialized hotspots (for React state / canvas rendering)
      const firstSlide = {
        id: `slide-${Date.now().toString(36)}`,
        label: 'Slide 1', order: 0, isActive: true,
        bgType:     activeLobby?.bgType || 'image',
        bgUrl:      activeLobby?.bgUrl  || null,
        responsive: activeLobby?.responsive
          ? Object.fromEntries(Object.entries(activeLobby.responsive).map(([k, v]) => [k, { ...v }]))
          : undefined,
        hotspots: existingHotspots,
        elements: existingElements,
      };

      // For the server we serialize back to the DB schema
      const firstSlideForServer = {
        ...firstSlide,
        hotspots: existingHotspots.map(serializeLobbyHotspot),
        elements: existingElements.map(serializeLobbyElement),
      };

      // Update React state with deserialized slide (canvas reads directly from here)
      setLobbies(prev => prev.map(l => l._id === activeLobbyId
        ? { ...l, lobbyType: 'carousel', slides: [firstSlide], bgUrl: null, bgType: 'image', responsive: {}, hotspots: [], elements: [] }
        : l
      ));
      // Clear the flat hotspot store for this lobby — carousel reads from slides now
      setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: [] }));
      // Reset undo history for this lobby
      setHistoryByLobby(prev => ({ ...prev, [activeLobbyId]: { stack: [[]], idx: 0 } }));
      // Persist serialized form to server
      api.put(`/lobbies/${activeLobbyId}`, {
        lobbyType: 'carousel', slides: [firstSlideForServer],
        bgUrl: null, bgType: 'image', responsive: {}, hotspots: [], elements: [],
      }).catch(() => {});

      setActiveSlideIndex(0);
      selectId('canvas');
      return;
    }

    const id = uid();
    let h;
    if (type === 'chat') {
      h = { id, label: 'Chat', shape: 'rect', points: [{ x: coords.x - 5, y: coords.y - 5 }], meta: { width: 10, height: 10 }, hotspotType: 'chat', hotspotAction: { channelId: null, chatTitle: 'Live Chat', panelWidth: 'md', showParticipantCount: true }, glowColor: SHAPE_TYPES.chat.color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    } else if (type === 'exhibitor-grid') {
      // Block if grid already exists anywhere in the lobby — including all carousel slides
      const hasGridInHotspots = hotspots.some(h => h.hotspotType === 'exhibitor-grid');
      const hasGridInSlides   = isCarousel && (activeLobby?.slides || []).some(s =>
        [...(s.hotspots || []), ...(s.elements || [])].some(h => h.elementType === 'exhibitor-grid' || h.hotspotType === 'exhibitor-grid')
      );
      if (hasGridInHotspots || hasGridInSlides) return;
      h = { id, label: 'Exhibitor Grid', shape: 'rect', points: [{ x: coords.x - 40, y: coords.y - 37.5 }], meta: { width: 80, height: 75, lobbyId: activeLobbyId, gridTileSize: 200, gridGap: 8, gridCardTemplate: 'flat' }, hotspotType: 'exhibitor-grid', hotspotAction: { exhibitionId: null, lobbyId: activeLobbyId }, glowColor: '#0d7a5f', glowOpacity: 0, order: hotspots.length, isActive: true };
    } else if (type === 'livestream-chat') {
      if (hotspots.some(h => h.hotspotType === 'livestream-chat')) return;
      const side = coords.x < 50 ? 'left' : 'right';
      const chatW = 22;
      const chatX = side === 'left' ? 0 : 100 - chatW;
      h = { id, label: 'Livestream Chat', shape: 'rect', points: [{ x: chatX, y: 0 }], meta: { width: chatW, height: 100, side }, hotspotType: 'livestream-chat', hotspotAction: { channelId: null, chatTitle: 'Livestream Chat' }, glowColor: '#0a7aff', glowOpacity: 0, order: hotspots.length, isActive: true };
    } else if (type === 'stream') {
      if (hotspots.some(h => h.hotspotType === 'stream')) return;
      h = { id, label: 'Livestream Player', shape: 'rect', points: [{ x: coords.x - 17.5, y: coords.y - 17.5 }], meta: { width: 35, height: 35 }, hotspotType: 'stream', hotspotAction: { streamType: 'iframe', streamUrl: '', viewerCount: 0 }, glowColor: SHAPE_TYPES.stream.color, glowOpacity: 0, order: hotspots.length, isActive: true };
    } else if (type === 'rect' || type === 'link') {
      h = { id, label: SHAPE_TYPES[type].label, shape: 'rect', points: [{ x: coords.x - 5, y: coords.y - 5 }], meta: { width: 10, height: 10 }, hotspotType: 'link', hotspotAction: {}, glowColor: SHAPE_TYPES[type].color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    } else if (type === 'text') {
      h = { id, label: 'Text', shape: 'rect', points: [{ x: coords.x - 17.5, y: coords.y - 6 }], meta: { width: 35, height: 12, text: 'Double-click to edit', fontSize: 25, fontWeight: 600, color: '#ffffff', textAlign: 'center', bgColor: 'rgba(0,0,0,0.7)', bgStrokeColor: '#ffffff', bgStrokeWidth: 3 }, hotspotType: 'text', hotspotAction: {}, glowColor: '#7c3aed', glowOpacity: 0, order: hotspots.length, isActive: true };
    } else if (type === 'image') {
      h = { id, label: 'Image', shape: 'rect', points: [{ x: coords.x - 12.5, y: coords.y - 7 }], meta: { width: 25, height: 14, imageUrl: null, objectFit: 'cover', bgStrokeColor: 'transparent', bgStrokeWidth: 0, borderRadius: 0 }, hotspotType: 'image', hotspotAction: {}, glowColor: '#e67e22', glowOpacity: 0, order: hotspots.length, isActive: true };
    } else if (type === 'circle') {
      h = { id, label: 'Circle Hotspot', shape: 'circle', points: [{ x: coords.x, y: coords.y }], meta: { radius: 5 }, hotspotType: 'link', hotspotAction: {}, glowColor: SHAPE_TYPES.circle.color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    } else {
      h = { id, label: 'Hotspot', shape: 'polygon', points: [{ x: coords.x - 5, y: coords.y - 5 }, { x: coords.x + 5, y: coords.y - 5 }, { x: coords.x + 5, y: coords.y + 5 }, { x: coords.x - 5, y: coords.y + 5 }], meta: {}, hotspotType: 'link', hotspotAction: {}, glowColor: SHAPE_TYPES.polygon.color, glowOpacity: 0.5, order: hotspots.length, isActive: true };
    }
    pushHotspots([...hotspots, h]);
    setSelectedId(id);
  }, [activeLobbyId, hotspots, pushHotspots, isCarousel, activeLobby]);

  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    const type = dragTypeRef.current;
    dragTypeRef.current = null;
    setGhost(null);
    if (!type || !activeLobbyId) return;
    const coords = getCanvasCoords(e);
    if (!coords) return;
    createElementAtCoords(type, coords);
  }, [activeLobbyId, getCanvasCoords, createElementAtCoords]);

  // ── Click-to-add: place element at canvas centre ──────────────────────────
  const handlePaletteClick = useCallback((type) => {
    createElementAtCoords(type, { x: 50, y: 50 });
  }, [createElementAtCoords]);

  // ── Hotspot move ──────────────────────────────────────────────────────────
  const startHotspotMove = useCallback((e, hotspotId) => {
    const startCoords = getCanvasCoords(e);
    if (!startCoords) return;
    const hotspot = hotspots.find(h => h.id === hotspotId);
    if (!hotspot) return;

    const isDesktop = breakpoint === 'desktop';
    // For non-desktop we drag from the resolved effective position for this bp
    const origBase = isDesktop
      ? JSON.parse(JSON.stringify(hotspot))
      : JSON.parse(JSON.stringify(resolveHotspotForBp(hotspot, breakpoint)));
    const origFull = JSON.parse(JSON.stringify(hotspot));

    const onMove = (ev) => {
      const cur = getCanvasCoords(ev);
      if (!cur) return;
      const moved = translateHotspot(origBase, cur.x - startCoords.x, cur.y - startCoords.y);
      if (isDesktop) {
        const updated = h => h.id === hotspotId ? moved : h;
        setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: (prev[activeLobbyId] || []).map(updated) }));
        if (isCarousel) pushCarouselHotspots(activeSlideIndex, (activeLobby?.slides?.[activeSlideIndex] ? [...(activeLobby.slides[activeSlideIndex].hotspots || []), ...(activeLobby.slides[activeSlideIndex].elements || [])] : []).map(updated));
      } else {
        const patched = setHotspotBpOverride(origFull, breakpoint, { points: moved.points, meta: moved.meta });
        const updPatched = h => h.id === hotspotId ? patched : h;
        setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: (prev[activeLobbyId] || []).map(updPatched) }));
        if (isCarousel) pushCarouselHotspots(activeSlideIndex, (activeLobby?.slides?.[activeSlideIndex] ? [...(activeLobby.slides[activeSlideIndex].hotspots || []), ...(activeLobby.slides[activeSlideIndex].elements || [])] : []).map(updPatched));
      }
    };
    const onUp = () => {
      if (isCarousel) {
        // for carousel the slide is already up-to-date from onMove; nothing extra needed
      } else {
        setHotspotsByLobby(prev => {
          const list = prev[activeLobbyId] || [];
          setHistoryByLobby(hb => { const h = hb[activeLobbyId] || { stack: [[]], idx: 0 }; return { ...hb, [activeLobbyId]: { stack: [...h.stack.slice(0, h.idx + 1), list], idx: h.idx + 1 } }; });
          return prev;
        });
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [hotspots, activeLobbyId, getCanvasCoords, breakpoint]);

  const handleHotspotMouseDown = useCallback((e, hotspotId) => {
    e.stopPropagation();
    // selectId(hotspotId);
    startHotspotMove(e, hotspotId);
  }, [startHotspotMove]);

  const handleGizmoBodyMouseDown = useCallback((e) => {
    e.stopPropagation();
    if (selectedId && selectedId !== 'canvas') startHotspotMove(e, selectedId);
  }, [selectedId, startHotspotMove]);

  // ── Resize handle drag ────────────────────────────────────────────────────
  const handleHandleMouseDown = useCallback((e, handleKey) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedId || selectedId === 'canvas') return;
    const hotspot = hotspots.find(h => h.id === selectedId);
    if (!hotspot) return;
    const startCoords = getCanvasCoords(e);
    if (!startCoords) return;

    const isDesktop = breakpoint === 'desktop';
    const origBase = isDesktop
      ? JSON.parse(JSON.stringify(hotspot))
      : JSON.parse(JSON.stringify(resolveHotspotForBp(hotspot, breakpoint)));
    const origFull = JSON.parse(JSON.stringify(hotspot));

    const onMove = (ev) => {
      const cur = getCanvasCoords(ev);
      if (!cur) return;
      const resized = applyResize(origBase, handleKey, cur.x - startCoords.x, cur.y - startCoords.y);
      if (isDesktop) {
        const updResized = h => h.id === selectedId ? resized : h;
        setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: (prev[activeLobbyId] || []).map(updResized) }));
        if (isCarousel) pushCarouselHotspots(activeSlideIndex, (activeLobby?.slides?.[activeSlideIndex] ? [...(activeLobby.slides[activeSlideIndex].hotspots || []), ...(activeLobby.slides[activeSlideIndex].elements || [])] : []).map(updResized));
      } else {
        const patched = setHotspotBpOverride(origFull, breakpoint, { points: resized.points, meta: resized.meta });
        const updPatched = h => h.id === selectedId ? patched : h;
        setHotspotsByLobby(prev => ({ ...prev, [activeLobbyId]: (prev[activeLobbyId] || []).map(updPatched) }));
        if (isCarousel) pushCarouselHotspots(activeSlideIndex, (activeLobby?.slides?.[activeSlideIndex] ? [...(activeLobby.slides[activeSlideIndex].hotspots || []), ...(activeLobby.slides[activeSlideIndex].elements || [])] : []).map(updPatched));
      }
    };
    const onUp = () => {
      if (isCarousel) {
        // carousel slide already updated live from onMove
      } else {
        setHotspotsByLobby(prev => {
          const list = prev[activeLobbyId] || [];
          setHistoryByLobby(hb => { const h = hb[activeLobbyId] || { stack: [[]], idx: 0 }; return { ...hb, [activeLobbyId]: { stack: [...h.stack.slice(0, h.idx + 1), list], idx: h.idx + 1 } }; });
          return prev;
        });
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [selectedId, hotspots, activeLobbyId, getCanvasCoords, breakpoint]);

  // ── Responsive lobby update (bg per-breakpoint) ───────────────────────────
  const handleUpdateLobbyResponsive = useCallback((bp, patch) => {
    if (!activeLobbyId || !bp || bp === 'desktop') return;
    setLobbies(prev => prev.map(l => {
      if (l._id !== activeLobbyId) return l;
      const existing = l.responsive?.[bp] || {};
      return { ...l, responsive: { ...(l.responsive || {}), [bp]: { ...existing, ...patch } } };
    }));
    const lobby = lobbies.find(l => l._id === activeLobbyId);
    if (!lobby) return;
    const existing = lobby.responsive?.[bp] || {};
    const updated = { responsive: { ...(lobby.responsive || {}), [bp]: { ...existing, ...patch } } };
    api.put(`/lobbies/${activeLobbyId}`, updated).catch(() => {});
  }, [activeLobbyId, lobbies]);

  // ── Responsive hotspot update (position/size per-breakpoint) ─────────────
  const handleUpdateHotspotResponsive = useCallback((hotspotId, bp, patch) => {
    if (!activeLobbyId || !bp || bp === 'desktop') return;
    const updated = hotspots.map(h => {
      if (h.id !== hotspotId) return h;
      return setHotspotBpOverride(h, bp, patch);
    });
    pushHotspots(updated);
  }, [activeLobbyId, hotspots, pushHotspots]);

  // ── isWizard — hide toolbar when no bg AND no hotspots (includes no-lobby state) ──
  // Only applies on desktop breakpoint; on tablet/mobile the canvas is always shown.
  const isWizard = !isCarousel && !activeLobby?.bgUrl && (hotspots?.length ?? 0) === 0 && !previewMode && breakpoint === 'desktop';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', background: '#f0f4f8', position: 'relative' }}
      onClick={e => {
        if (e.target.closest && e.target.closest('svg')) return;
        setSelectedId(null);
        setHeaderSelected(false);
      }}
    >
      <DragGhost ghost={ghost} />

      <BuilderToolbar
        activeLobby={activeLobby}
        hist={hist}
        previewMode={previewMode}
        saveStatus={saveStatus}
        onUndo={undo}
        onRedo={redo}
        onSave={event?._id && !(activeExhibitionId && editingExhibitor) ? handleSave : null}
        onTogglePreview={async () => { await handleSave(); setPreviewMode(v => !v); setSelectedId(null); }}
        event={event}
        onUpdateLobby={handleUpdateLobby}
        onUpdateEvent={handleUpdateEvent}
        isWizard={isWizard}
        visitUrl={event?.slug && activeLobby?.slug ? `${process.env.REACT_APP_FRONTEND_URL || ''}/${event.slug}/${activeLobby.slug}` : null}
        onVisit={async () => {
          await handleSave();
          const url = event?.slug && activeLobby?.slug ? `${process.env.REACT_APP_FRONTEND_URL || ''}/${event.slug}/${activeLobby.slug}` : null;
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
        }}
        breakpoint={breakpoint}
        onSetBreakpoint={setBreakpoint}
      />

      {/* ── Preview overlay ── */}
      {previewMode && activeLobbyId && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 50, background: '#0a0e1a', display: 'flex', flexDirection: 'column' }}>

          {/* Preview toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: 48, background: '#fff', flexShrink: 0, borderBottom: '1px solid #e8edf2' }}>

            {/* Left — lobby name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#3c4555' }}>
                {activeLobby?.name}
              </Typography>
              <Box sx={{ px: 0.75, py: 0.2, borderRadius: '4px', background: 'rgba(34,197,94,0.1)', color: '#16a34a', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                PREVIEW
              </Box>
            </Box>

            {/* Center — breakpoint switcher */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', background: '#f0f4f8', border: '1px solid #e4e9f0', borderRadius: 2, p: '2px', gap: '2px' }}>
                {/* Desktop */}
                {(() => {
                  const bpDef = LOBBY_BREAKPOINTS[0];
                  const active = breakpoint === bpDef.key;
                  return (
                    <Tooltip title={bpDef.label} key={bpDef.key}>
                      <IconButton size="small" onClick={() => setBreakpoint(bpDef.key)}
                        sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? PRIMARY : '#9ea8b3', background: active ? '#fff' : 'transparent', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                        {bpDef.icon}
                      </IconButton>
                    </Tooltip>
                  );
                })()}

                <Box sx={{ width: '1px', height: 16, background: '#e4e9f0', mx: '1px' }} />

                {/* Tablet */}
                {LOBBY_BREAKPOINTS.filter(b => b.group === 'tablet').map(bpDef => {
                  const active = breakpoint === bpDef.key;
                  return (
                    <Tooltip key={bpDef.key} title={bpDef.label}>
                      <IconButton size="small" onClick={() => setBreakpoint(bpDef.key)}
                        sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? '#b45309' : '#9ea8b3', background: active ? '#fffbeb' : 'transparent', boxShadow: active ? '0 1px 3px rgba(245,158,11,0.2)' : 'none' }}>
                        {bpDef.icon}
                      </IconButton>
                    </Tooltip>
                  );
                })}

                <Box sx={{ width: '1px', height: 16, background: '#e4e9f0', mx: '1px' }} />

                {/* Mobile */}
                {(() => {
                  const bpDef = LOBBY_BREAKPOINTS.find(b => b.key === 'mobile');
                  const active = breakpoint === bpDef.key;
                  return (
                    <Tooltip title={bpDef.label} key={bpDef.key}>
                      <IconButton size="small" onClick={() => setBreakpoint(bpDef.key)}
                        sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? '#b91c1c' : '#9ea8b3', background: active ? '#fff5f5' : 'transparent', boxShadow: active ? '0 1px 3px rgba(239,68,68,0.2)' : 'none' }}>
                        {bpDef.icon}
                      </IconButton>
                    </Tooltip>
                  );
                })()}
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8', fontFamily: 'monospace' }}>
                {LOBBY_BREAKPOINTS.find(b => b.key === breakpoint)?.canvasW} × {LOBBY_BREAKPOINTS.find(b => b.key === breakpoint)?.canvasH}
              </Typography>
            </Box>

            {/* Right — exit button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 160 }}>
              <Box onClick={() => setPreviewMode(false)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '8px', border: '1px solid #e8edf2', background: '#f8f9fb', color: '#5a6474', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { background: '#fee2e2', borderColor: '#fca5a5', color: '#dc2626' } }}>
                <VisibilityOffOutlined sx={{ fontSize: 15 }} />
                Exit Preview
              </Box>
            </Box>
          </Box>

          {/* Preview canvas — device frame scales to fit available space without scrolling */}
          <PreviewCanvas activeLobbyId={activeLobbyId} breakpoint={breakpoint} />
        </Box>
      )}

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left panel ── */}
        {!previewMode && !(activeExhibitionId && editingExhibitor) && (
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ width: 230, flexShrink: 0, borderRight: '1px solid #e8edf2', background: '#fafbfc', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            {activeExhibitionId ? (
              <ExhibitionInspector
                exhibition={exhibitionPages.find(p => p._id === activeExhibitionId) || null}
                onUpdate={handleUpdateExhibition}
                onClose={() => {
                  setActiveExhibitionId(null);
                  setSelectedExhibitorId(null);
                  setEditingExhibitor(null);
                  const fallbackId = lobbies[0]?._id || null;
                  if (fallbackId) { setActiveLobbyId(fallbackId); setSearchParams({ id: fallbackId }, { replace: true }); }
                  else setSearchParams({}, { replace: true });
                }}
                selectedExhibitor={selectedExhibitor}
                onUpdateExhibitor={handleUpdateExhibitor}
                onDeleteExhibitor={() => selectedExhibitorId && handleDeleteExhibitorFromHall(selectedExhibitorId)}
                onEditBooth={() => selectedExhibitor && handleOpenBoothEditor(selectedExhibitor)}
                onDeselectExhibitor={() => setSelectedExhibitorId(null)}
              />
            ) : activePlenaryId ? (
              <PlenaryInspector
                plenary={plenaryPages.find(p => p._id === activePlenaryId) || null}
                selectedComponent={selectedPlenaryComp || 'page'}
                onUpdate={handleUpdatePlenary}
                onClose={() => setSelectedPlenaryComp(null)}
              />
            ) : selectedHotspot ? (
              <HotspotInspector
                hotspot={selectedHotspot}
                lobbies={lobbies}
                plenaries={plenaryPages}
                exhibitors={selectedHotspot?.hotspotType === 'exhibitor-grid' ? lobbyExhibitors : []}
                selectedLobbyExhibitorId={selectedLobbyExhibitorId}
                onSelectLobbyExhibitor={setSelectedLobbyExhibitorId}
                onUpdate={handleUpdateHotspot}
                onSave={(updatedHotspot) => {
                  // Build the fresh hotspot list right here — don't rely on stale closure in handleSave
                  const freshHotspots = updatedHotspot
                    ? hotspots.map(h => h.id === updatedHotspot.id ? updatedHotspot : h)
                    : hotspots;
                  return handleSave(freshHotspots);
                }}
                onDelete={() => handleDeleteHotspot(selectedHotspot.id)}
                onClose={() => selectId(null)}
                onAddExhibitor={handleAddExhibitorToHall}
                onDeleteExhibitor={handleDeleteExhibitorFromHall}
                onUpdateExhibitor={handleUpdateExhibitor}
                breakpoint={breakpoint}
                onUpdateResponsive={handleUpdateHotspotResponsive}
              />
            ) : headerSelected ? (
              <LobbyHeaderInspector
                config={lobbyHeaderConfig}
                onChange={handleLobbyHeaderChange}
                onClose={() => setHeaderSelected(false)}
                lobbies={lobbies}
                activeLobby={activeLobby}
                onChangeLobby={handleUpdateLobby}
              />
            ) : canvasSelected ? (
              <CanvasInspector
                lobby={activeLobby}
                onUpdateLobby={handleUpdateLobby}
                onUpdateLobbyResponsive={handleUpdateLobbyResponsive}
                onClose={() => setSelectedId(null)}
                breakpoint={breakpoint}
                activeSlideIndex={activeSlideIndex}
                onSelectSlide={(idx) => { navigateToSlide(idx, idx > activeSlideIndex ? 'right' : 'left'); setSelectedId('canvas'); }}
                onAddSlide={handleAddSlide}
                onDeleteSlide={handleDeleteSlide}
                onReorderSlides={handleReorderSlides}
                onUpdateSlide={handleUpdateSlide}
                onUpdateSlideResponsive={handleUpdateSlideResponsive}
                onRemoveCarousel={handleRemoveCarousel}
                onConvertToCarousel={handleConvertToCarousel}
              />
            ) : (
              <ElementPalette
                onDragStart={handlePaletteDragStart}
                onClick={handlePaletteClick}
                existingElementTypes={(() => {
                  const types = hotspots.filter(h => h.hotspotType === 'livestream-chat' || h.hotspotType === 'stream' || h.hotspotType === 'exhibitor-grid').map(h => h.hotspotType);
                  // For carousel: if any slide in the lobby has an exhibitor-grid, disable it globally
                  if (isCarousel && (activeLobby?.slides || []).some(s =>
                    [...(s.hotspots || []), ...(s.elements || [])].some(h => h.elementType === 'exhibitor-grid' || h.hotspotType === 'exhibitor-grid')
                  )) {
                    if (!types.includes('exhibitor-grid')) types.push('exhibitor-grid');
                  }
                  return types;
                })()}
                lobbyType={activeLobby?.lobbyType}
              />
            )}
          </Box>
        )}

        {/* ── Center column ── */}
        <Box
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
          onClick={() => { setHeaderSelected(false); }}
        >
          {/* ── Back to Exhibitor button — shown when active lobby is a booth ── */}
          {activeLobby?.lobbyType === 'booth' && activeLobby?.connectedExhibitor && (() => {
            const allExhibitors = [...lobbyExhibitors, ...exhibitors];
            const linkedEx = allExhibitors.find(ex => String(ex.boothLobbyId) === String(activeLobbyId));
            const gridSourceLobby = lobbies.find(l => (hotspotsByLobby[l._id] || []).some(h => h.hotspotType === 'exhibitor-grid'));
            return (
              <Box
                onClick={e => {
                  e.stopPropagation();
                  if (gridSourceLobby) {
                    setActiveLobbyId(gridSourceLobby._id);
                    setSelectedId(null);
                    setSelectedLobbyExhibitorId(linkedEx ? String(linkedEx._id) : null);
                    setSearchParams({ id: gridSourceLobby._id }, { replace: true });
                  }
                }}
                sx={{
                  position: 'absolute', top: 12, left: 12, zIndex: 50,
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.75, borderRadius: '8px',
                  background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(4px)',
                  border: '1px solid #e4e9f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: '#3c4555',
                  transition: 'all 0.15s',
                  '&:hover': { background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', color: '#1075BA' },
                }}
              >
                <Box sx={{ fontSize: 14, lineHeight: 1 }}>‹</Box>
                Back to Exhibitor Page
              </Box>
            );
          })()}

          {/* ── Delete lobby confirm dialog ── */}
          {deleteLobbyConfirm && (
            <Box onClick={e => e.stopPropagation()} sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
              <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Box sx={{ fontSize: 18 }}>⚠️</Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>Delete lobby?</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>{deleteLobbyConfirm.lobbyName}</Box> has{' '}
                      <Box component="span" sx={{ fontWeight: 600 }}>{deleteLobbyConfirm.exhibitorCount} exhibitor{deleteLobbyConfirm.exhibitorCount !== 1 ? 's' : ''}</Box>{' '}
                      attached to its grid. If you continue, these exhibitors and any linked booth lobbies will be permanently deleted.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Box onClick={() => setDeleteLobbyConfirm(null)} sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', '&:hover': { background: '#f8f9fb' } }}>Cancel</Box>
                  <Box onClick={() => commitDeleteLobby(deleteLobbyConfirm.id)} sx={{ px: 2, py: 0.85, borderRadius: '8px', background: '#dc2626', fontSize: '0.8rem', fontWeight: 700, color: '#fff', cursor: 'pointer', '&:hover': { background: '#b91c1c' } }}>Delete Lobby & Exhibitors</Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── Delete exhibitor confirm dialog ── */}
          {deleteExhibitorConfirm && (
            <Box onClick={e => e.stopPropagation()} sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
              <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 380, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Box sx={{ fontSize: 18 }}>⚠️</Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>Delete exhibitor?</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>{deleteExhibitorConfirm.name}</Box> has a linked Exhibitor Lobby. Deleting this exhibitor will also permanently delete that lobby and all its content.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Box onClick={() => setDeleteExhibitorConfirm(null)} sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', '&:hover': { background: '#f8f9fb' } }}>Cancel</Box>
                  <Box onClick={() => handleDeleteExhibitorFromHall(deleteExhibitorConfirm._id, true)} sx={{ px: 2, py: 0.85, borderRadius: '8px', background: '#dc2626', fontSize: '0.8rem', fontWeight: 700, color: '#fff', cursor: 'pointer', '&:hover': { background: '#b91c1c' } }}>Delete Exhibitor & Lobby</Box>
                </Box>
              </Box>
            </Box>
          )}


          {/* ── Delete livestream element confirm dialog ── */}
          {deleteStreamElementConfirm && (
            <Box onClick={e => e.stopPropagation()} sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
              <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 420, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Box sx={{ fontSize: 18 }}>⚠️</Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>
                      {deleteStreamElementConfirm.isActive ? 'Stream is currently live' : 'Delete livestream player?'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>
                      {deleteStreamElementConfirm.isActive
                        ? <>You cannot delete this element while the stream is active. Please <Box component="span" sx={{ fontWeight: 700, color: '#dc2626' }}>turn off the livestream first</Box> in the inspector panel, then try deleting again.</>
                        : deleteStreamElementConfirm.hasMux
                          ? <>This element has a Mux livestream attached. Deleting the element will also <Box component="span" sx={{ fontWeight: 600 }}>permanently delete the stream</Box> and all its credentials.</>
                          : <>This will permanently remove the livestream player element from the canvas.</>
                      }
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Box
                    onClick={() => { if (!deletingStreamElement) setDeleteStreamElementConfirm(null); }}
                    sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: deletingStreamElement ? 'not-allowed' : 'pointer', opacity: deletingStreamElement ? 0.5 : 1, '&:hover': { background: '#f8f9fb' } }}
                  >Cancel</Box>
                  {!deleteStreamElementConfirm.isActive && (
                    <Box
                      onClick={async () => {
                        if (deletingStreamElement) return;
                        const { id, hasMux, muxStreamId } = deleteStreamElementConfirm;
                        setDeletingStreamElement(true);
                        try {
                          if (hasMux && muxStreamId) {
                            await api.delete(`/mux/livestreams/${muxStreamId}/delete`).catch(() => {});
                          }
                          // Build the post-delete hotspot list right here and pass it
                          // directly to handleSave — don't rely on async state flush
                          const freshHotspots = hotspots.filter(h => h.id !== id);
                          setDeleteStreamElementConfirm(null);
                          commitDeleteHotspot(id);
                          if (selectedId === id) setSelectedId(null);
                          await handleSave(freshHotspots);
                        } finally {
                          setDeletingStreamElement(false);
                        }
                      }}
                      sx={{ px: 2, py: 0.85, borderRadius: '8px', background: deletingStreamElement ? '#f87171' : '#dc2626', fontSize: '0.8rem', fontWeight: 700, color: '#fff', cursor: deletingStreamElement ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 0.75, '&:hover': { background: deletingStreamElement ? '#f87171' : '#b91c1c' } }}
                    >
                      {deletingStreamElement && <CircularProgress size={12} sx={{ color: '#fff' }} />}
                      {deletingStreamElement ? 'Deleting…' : deleteStreamElementConfirm.hasMux ? 'End Stream & Delete Element' : 'Delete Element'}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* ── Main content area ── */}
          {activeExhibitionId ? (
            editingExhibitor ? (
              <ExhibitorBoothEditor exhibitor={editingExhibitor} onBack={handleCloseBoothEditor} />
            ) : (
              <ExhibitorCanvas
                event={event}
                exhibition={exhibitionPages.find(p => p._id === activeExhibitionId) || null}
                exhibitors={lobbyExhibitors}
                selectedLobbyExhibitorId={selectedLobbyExhibitorId}
                onSelectLobbyExhibitor={setSelectedLobbyExhibitorId}
                onAddExhibitor={handleAddExhibitorToHall}
                loading={exhibitorsLoading}
                selectedExhibitorId={selectedExhibitorId}
                onSelectExhibitor={setSelectedExhibitorId}
                onReorder={handleReorderExhibitors}
                onDoubleClick={handleOpenBoothEditor}
              />
            )
          ) : activePlenaryId ? (
            <PlenaryCanvas
              plenary={plenaryPages.find(p => p._id === activePlenaryId) || null}
              selectedComponent={selectedPlenaryComp}
              onSelectComponent={setSelectedPlenaryComp}
              previewMode={previewMode}
            />
          ) : (
            // ── Lobby canvas — also shown when activeLobbyId is null (no lobbies yet)
            // In that case lobby=null, LobbyCanvas shows the wizard, and
            // onCreateFirstLobby creates the first lobby from the background selection.
            <LobbyCanvas
              canvasRef={canvasRef}
              lobby={isCarousel && activeSlide
                ? { ...activeLobby, bgUrl: activeSlide.bgUrl, bgType: activeSlide.bgType || 'image', responsive: activeSlide.responsive }
                : activeLobby}
              slideTrack={isCarousel && slideTransition ? {
                currentBgUrl:  activeSlide?.bgUrl  || null,
                currentBgType: activeSlide?.bgType || 'image',
                incomingBgUrl:  (activeLobby?.slides?.[slideTransition.toIndex]?.bgUrl)  || null,
                incomingBgType: (activeLobby?.slides?.[slideTransition.toIndex]?.bgType) || 'image',
                direction: slideTransition.direction,
                phase:     slideTransition.phase,
              } : null}
              incomingHotspots={isCarousel && slideTransition ? (() => {
                const s = activeLobby?.slides?.[slideTransition.toIndex];
                return s ? [...(s.hotspots || []), ...(s.elements || [])] : [];
              })() : null}
              overlayContent={isCarousel && !previewMode && (activeLobby?.slides?.length ?? 0) > 0 ? (
                <>
                  {/* ── Left arrow ────────────────────────────────────────── */}
                  {!activeSlide?.hideNav && activeSlideIndex > 0 && (
                    <Box
                      onClick={e => { e.stopPropagation(); navigateToSlide(activeSlideIndex - 1, 'left'); }}
                      sx={{
                        position: 'absolute', left: '2%', top: '50%', transform: 'translateY(-50%)',
                        zIndex: 16, width: 45, height: 45, borderRadius: '50%',
                        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#fff', userSelect: 'none',
                        transition: 'all 0.15s',
                        '&:hover': { background: 'rgba(15,23,42,0.82)', borderColor: 'rgba(255,255,255,0.35)', transform: 'translateY(-50%) scale(1.08)' },
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 25, lineHeight: 1, mt: '-1px' }}>‹</Box>
                    </Box>
                  )}

                  {/* ── Right arrow ───────────────────────────────────────── */}
                  {!activeSlide?.hideNav && activeSlideIndex < (activeLobby?.slides?.length ?? 1) - 1 && (
                    <Box
                      onClick={e => { e.stopPropagation(); navigateToSlide(activeSlideIndex + 1, 'right'); }}
                      sx={{
                        position: 'absolute', right: '2%', top: '50%', transform: 'translateY(-50%)',
                        zIndex: 16, width: 45, height: 45, borderRadius: '50%',
                        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#fff', userSelect: 'none',
                        transition: 'all 0.15s',
                        '&:hover': { background: 'rgba(15,23,42,0.82)', borderColor: 'rgba(255,255,255,0.35)', transform: 'translateY(-50%) scale(1.08)' },
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 25, lineHeight: 1, mt: '-1px' }}>›</Box>
                    </Box>
                  )}

                  {/* ── Dot nav ───────────────────────────────────────────── */}
                  {!activeSlide?.hideNav && <Box
                    onClick={e => e.stopPropagation()}
                    sx={{
                      position: 'absolute', bottom: '3%', left: '50%', transform: 'translateX(-50%)',
                      zIndex: 16, display: 'flex', alignItems: 'center', gap: '7.5px',
                      px: 1.5, py: '6px', borderRadius: '20px',
                      background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      pointerEvents: 'all',
                    }}
                  >
                    {(activeLobby?.slides || []).map((slide, idx) => {
                      const isActive = slideTransition ? idx === slideTransition.toIndex : idx === activeSlideIndex;
                      return (
                        <Box
                          key={slide.id}
                          onClick={() => { navigateToSlide(idx, idx > activeSlideIndex ? 'right' : 'left'); }}
                          sx={{
                            width: isActive ? 25 : 7.5, height: 7.5, borderRadius: '4px',
                            background: isActive ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.4)',
                            cursor: 'pointer', transition: 'all 0.25s', flexShrink: 0,
                            '&:hover': { background: 'rgba(255,255,255,0.9)' },
                          }}
                        />
                      );
                    })}
                  </Box>}
                </>
              ) : null}
              hotspots={hotspots}
              selectedId={selectedId}
              canvasSelected={canvasSelected}
              previewMode={false}
              ghost={ghost}
              dragTypeRef={dragTypeRef}
              breakpoint={breakpoint}
              onCanvasClick={e => {
                if (e.target.closest && e.target.closest('svg')) return;
                e.stopPropagation();
                if (selectedId && selectedId !== 'canvas') { selectId(null); }
                else if (isCarousel && !canvasSelected) { selectId('canvas'); }
                else { selectId('canvas'); }
              }}
              onCanvasDragOver={e => { if (dragTypeRef.current) e.preventDefault(); }}
              onCanvasDrop={handleCanvasDrop}
              onHotspotMouseDown={handleHotspotMouseDown}
              onHotspotClick={id => selectId(id)}
              onHandleMouseDown={handleHandleMouseDown}
              onGizmoBodyMouseDown={handleGizmoBodyMouseDown}
              onHotspotUpdate={handleUpdateHotspot}
              onUpdateLobby={patch => {
                if (isCarousel && (patch.bgUrl !== undefined || patch.bgType !== undefined)) {
                  handleUpdateSlide(activeSlideIndex, patch);
                } else {
                  handleUpdateLobby(patch);
                }
              }}
              lobbies={lobbies}
              lobbyHeaderConfig={lobbyHeaderConfig}
              headerSelected={headerSelected}
              onSelectHeader={() => { setHeaderSelected(true); setSelectedId(null); }}
              onChangeLinks={links => handleLobbyHeaderChange({ ...lobbyHeaderConfigRef.current, links })}
              onChangeConfig={handleLobbyHeaderChange}
              activeTool={activeTool}
              onSelectTool={handleSelectTool}
              penTool={penTool}
              pathEdit={pathEdit}
              drawState={drawState}
              onDrawMouseDown={handleDrawMouseDown}
              onDrawMouseMove={handleDrawMouseMove}
              onDrawMouseUp={handleDrawMouseUp}
              onOpenChatModal={openChatModal}
              exhibitors={lobbyExhibitors}
              selectedLobbyExhibitorId={selectedLobbyExhibitorId}
              onSelectLobbyExhibitor={id => { setSelectedLobbyExhibitorId(id); }}
              onUpdateExhibitor={handleUpdateExhibitor}
              onAddSlideForExhibitor={handleAddSlideForExhibitor}
              activeSlideIndex={activeSlideIndex}
              onBackToFirstSlide={isCarousel && activeSlideIndex > 0 ? () => navigateToSlide(0, 'left') : undefined}
              onAddExhibitor={handleAddExhibitorToHall}
              onDeleteExhibitor={handleDeleteExhibitorFromHall}
              onCreateExhibitorLobby={handleCreateExhibitorLobby}
              onGoToExhibitorLobby={handleGoToExhibitorLobby}
              onReorderExhibitors={async (reordered) => {
                setLobbyExhibitors(reordered);
                reordered.forEach(ex => { api.put(`/exhibitors/${ex._id}`, { order: ex.order }).catch(() => { }); });
              }}
              zoom={canvasZoom}
              onCreateFirstLobby={handleAddLobby}
              onDeleteHotspot={handleDeleteHotspot}
              defaultLogoSrc={event?.layout?.header?.logoSrc || ''}
            />
          )}

          {/* ── Canvas bottom bar: zoom | toolbar | chat ── */}
          {!previewMode && !(activeExhibitionId) && activeLobbyId && (activeLobby?.bgUrl || isCarousel) && (
            <Box
              onClick={e => e.stopPropagation()}
              sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, overflowX: 'auto', gap: 0, '&::-webkit-scrollbar': { height: 3 }, '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 } }}
            >
              {/* Column 1 — Zoom controls */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box onClick={() => setCanvasZoom(z => Math.max(0.25, parseFloat((z - 0.1).toFixed(2))))}
                  sx={{ width: 26, height: 26, borderRadius: '6px', border: '1px solid #e4e9f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: '#5a6474', userSelect: 'none', flexShrink: 0, '&:hover': { background: '#f0f4f8', borderColor: '#c4cdd8' } }}>−</Box>
                <Box onClick={() => setCanvasZoom(z => Math.min(3, parseFloat((z + 0.1).toFixed(2))))}
                  sx={{ width: 26, height: 26, borderRadius: '6px', border: '1px solid #e4e9f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: '#5a6474', userSelect: 'none', flexShrink: 0, '&:hover': { background: '#f0f4f8', borderColor: '#c4cdd8' } }}>+</Box>
                <Box onClick={() => setCanvasZoom(.9)}
                  sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9ea8b3', cursor: 'pointer', minWidth: 32, textAlign: 'center', flexShrink: 0, '&:hover': { color: '#1075BA' } }}>
                  {Math.round(canvasZoom * 100)}%
                </Box>
                <Box onClick={() => setCanvasZoom(.9)}
                  sx={{ px: 1, py: 0.4, borderRadius: '6px', border: '1px solid #e4e9f0', background: '#fff', fontSize: '0.68rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', userSelect: 'none', '&:hover': { background: '#f0f4f8', borderColor: '#c4cdd8', color: '#1075BA' } }}>
                  Reframe
                </Box>
              </Box>

              {/* Column 2 — Hotspot strip */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                <CanvasToolbar
                  hotspotContent={selectedHotspot && activeTool === 'pointer' ? (
                    <HotspotStripContents
                      hotspot={selectedHotspot}
                      lobbies={lobbies}
                      onUpdate={handleUpdateHotspot}
                      onDelete={selectedHotspot ? () => handleDeleteHotspot(selectedHotspot.id) : undefined}
                      breakpoint={breakpoint}
                      onUpdateResponsive={handleUpdateHotspotResponsive}
                      selectedLobbyExhibitorId={selectedLobbyExhibitorId}
                      exhibitors={lobbyExhibitors}
                      onUpdateExhibitor={handleUpdateExhibitor}
                      onDeleteExhibitor={handleDeleteExhibitorFromHall}
                      onGoToExhibitorLobby={handleGoToExhibitorLobby}
                      onCreateExhibitorLobby={handleCreateExhibitorLobby}
                      onAddSlide={handleAddSlideForExhibitor}
                      lobbySlides={isCarousel ? (activeLobby?.slides || []) : []}
                      isCarouselLobby={isCarousel}
                    />
                  ) : null}
                  activeTool={activeTool}
                  onSelectTool={handleSelectTool}
                  penPoints={penTool?.points?.length || 0}
                  onDiscardPen={penTool?.reset}
                  rightSlot={null}
                />
              </Box>

              {/* Column 3 — Chat button */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {(() => {
                  const chatHotspots = hotspots.filter(h => h.hotspotType === 'chat');
                  if (!chatHotspots.length) return null;
                  return (
                    <Box onClick={() => openChatModal()}
                      sx={{ width: 36, height: 36, borderRadius: '50%', background: '#0a7aff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', boxShadow: '0 4px 14px rgba(10,122,255,0.35)', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'scale(1.1)', boxShadow: '0 6px 20px rgba(10,122,255,0.5)' } }}>
                      <ChatBubbleOutlineOutlined sx={{ fontSize: 17 }} />
                      {chatHotspots.length > 1 && (
                        <Box sx={{ position: 'absolute', top: -3, right: -3, minWidth: 16, height: 16, borderRadius: '8px', background: '#fff', color: '#0a7aff', fontSize: '0.55rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.3, boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }}>
                          {chatHotspots.length}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}

          {/* ── Bottom tab strip ── */}
          {!previewMode && !(activeExhibitionId && editingExhibitor) && (
            <>
              <LobbyTabs
                lobbies={lobbies}
                activeLobbyId={activeLobbyId}
                onSwitch={handleSwitchLobby}
                onAdd={(type) => handleAddLobby({ lobbyType: type === 'exhibition' ? 'exhibition' : type, _seedType: type })}
                onRename={handleRenameLobby}
                onDuplicate={handleDuplicateLobby}
                onDelete={handleDeleteLobby}
                onSetDefault={handleSetDefaultLobby}
                onReorder={handleReorderLobbies}
                loading={lobbiesLoading}
                screenshots={screenshots}
              />
              <CreateLobbyOrPlenaryDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onLobbyCreated={handleAddLobby}
                event={event}
              />
            </>
          )}
        </Box>

        {/* ── Right panel: layers ── */}
        {!previewMode && !activeExhibitionId && (
          <LayersPanel
            hotspots={hotspots}
            selectedId={selectedId}
            onSelect={selectId}
            onDelete={handleDeleteHotspot}
            onReorder={reordered => pushHotspots(reordered)}
            lobbyType={activeLobby?.lobbyType}
            onRemoveCarousel={handleRemoveCarousel}
            onConvertToCarousel={handleConvertToCarousel}
            slides={isCarousel ? (activeLobby?.slides || []) : []}
            activeSlideIndex={activeSlideIndex}
            onSelectSlide={idx => { if (idx !== activeSlideIndex) { navigateToSlide(idx, idx > activeSlideIndex ? 'right' : 'left'); } selectId('canvas'); }}
            onDeleteSlide={handleDeleteSlide}
            onSelectSlideHotspot={(id, slideIdx) => { if (slideIdx !== activeSlideIndex) { navigateToSlide(slideIdx, slideIdx > activeSlideIndex ? 'right' : 'left'); } selectId(id); }}
            onDeleteSlideHotspot={(slideIdx, hotspotId, reorderedList) => {
              const slide = activeLobby?.slides?.[slideIdx];
              if (!slide) return;
              if (hotspotId === '__reorder__' && reorderedList) {
                // Reorder within slide
                const newHotspots = reorderedList.filter(h => !['stream','livestream-chat','exhibitor-grid'].includes(h.hotspotType || h.elementType));
                const newElements = reorderedList.filter(h => ['stream','livestream-chat','exhibitor-grid'].includes(h.hotspotType || h.elementType));
                handleUpdateSlide(slideIdx, { hotspots: newHotspots, elements: newElements });
              } else {
                const newHotspots = (slide.hotspots || []).filter(h => h.id !== hotspotId);
                const newElements = (slide.elements || []).filter(h => h.id !== hotspotId);
                handleUpdateSlide(slideIdx, { hotspots: newHotspots, elements: newElements });
              }
            }}
          />
        )}
      </Box>

      {/* ── Chat modal ── */}
      <ChatModal
        open={chatModalOpen}
        onClose={closeChatModal}
        chatHotspots={lobbyChatHotspots}
        activeChatHotspot={activeChatHotspot}
        event={event}
      />
    </Box>
  );
}